import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Layout } from '../../components/layout/Layout';
import { PageHeader } from '../../components/layout/PageHeader';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { RefreshCw, History, Pencil, Power, Plus, Trash2 } from 'lucide-react';
import {
  getPromptLibraryBlocks,
  getPromptLibraryBindings,
  getPromptLibraryDialects,
  getPromptLibraryTemplates,
  updatePromptLibraryTemplate,
  createPromptLibraryTemplate,
  updatePromptLibraryBlock,
  createPromptLibraryBlock,
  updateDialectProfileById,
  createDialectProfile,
  activatePromptTemplateVersion,
  activatePromptBlockVersion,
  activateDialectProfileVersion,
  deleteDialectProfileGroupById,
  deletePromptLibraryBlock,
  deletePromptLibraryTemplate,
  type DialectProfileAdmin,
  type PromptBindingAdmin,
  type PromptBlockAdmin,
  type PromptTemplateAdmin,
} from '../../modules/tutor/api/promptLibraryAdminService';
import { listSchools } from '../../modules/platform/api/devteam';
import type { School } from '../../modules/platform/types';
import { handleApiError, showErrorToast, showSuccessToast } from '../../utils/errorHandler';
import { ConfirmDialog } from './components/ConfirmDialog';
import { isLegacyPromptBlockKey } from '../../modules/tutor/promptLibraryLegacy';
import { PromptEditor } from '../../components/PromptEditor';
import {
  LESSON_TYPE_RULE_MAP_OPTION_ROWS,
  buildLessonTypeRuleMapFromSlots,
  emptyLessonTypeRuleSlots,
  messageForBindingOutcome,
  validateAutoBindingForm,
} from './promptBindingFormUtils';

type ActiveTab = 'template' | 'block' | 'dialect';

const TAB_LABELS: Record<ActiveTab, string> = {
  template: 'Templates',
  block: 'Blocks',
  dialect: 'Dialects',
};

const BLOCK_TYPES = [
  'base_rules',
  'lesson_type_rules',
  'mode_rules',
  'source_rules',
  'final_output_check',
  'communication_rules',
  'picture_talk_rules',
  'dialect_rules',
  'event_rules',
  'safety_rules',
] as const;
const BLOCK_TYPE_LABELS: Record<(typeof BLOCK_TYPES)[number], string> = {
  base_rules: '{{ base_rules }}',
  lesson_type_rules: '{{ lesson_type_rules }}',
  mode_rules: '{{ mode_rules }}',
  source_rules: '{{ source_rules }}',
  final_output_check: '{{ final_output_check }}',
  communication_rules: '{{ communication_rules }}',
  picture_talk_rules: '{{ picture_talk_rules }}',
  dialect_rules: '{{ dialect_rules }}',
  event_rules: '{{ event_rules }}',
  safety_rules: '{{ safety_rules }}',
};

const PICTURE_TALK_SUBMODULES = ['event_handling', 'item_select', 'scene_question', 'conversation'] as const;
const PICTURE_TALK_EVENT_TYPES = ['start', 'object_clicked', 'no_response_1', 'no_response_2', 'wrapup_40s', 'idle_no_click'] as const;
const PICTURE_TALK_MODES = ['LIVE'] as const;

const MODES = ['FREE', 'TOPIC', 'LESSON'] as const;

/** Default `module` for new AI Tutor blocks and templates (matches backend conventions). */
const DEFAULT_AI_TUTOR_MODULE = 'ai_tutor';

/** One row per logical entity: prefer active version(s), then highest version. */
function pickLatestPerGroup<T>(
  items: T[],
  groupKey: (t: T) => string,
  versionOf: (t: T) => number,
  statusOf: (t: T) => string
): T[] {
  const groups = new Map<string, T[]>();
  for (const item of items) {
    const k = groupKey(item);
    const arr = groups.get(k) ?? [];
    arr.push(item);
    groups.set(k, arr);
  }
  const out: T[] = [];
  for (const group of groups.values()) {
    const active = group.filter((x) => statusOf(x) === 'active');
    const pool = active.length > 0 ? active : group;
    const best = pool.reduce((a, b) => (versionOf(a) >= versionOf(b) ? a : b));
    out.push(best);
  }
  return out;
}

/** Runtime winner: among `active` rows pick highest version; if none active (bad data), pick highest version overall. */
function pickRuntimeWinnerId<T extends { id: string; version: number; status: string }>(rows: T[]): string | null {
  if (rows.length === 0) return null;
  const active = rows.filter((r) => r.status === 'active');
  const pool = active.length > 0 ? active : rows;
  return pool.reduce((a, b) => (a.version >= b.version ? a : b)).id;
}

function sameTemplateScope(a: PromptTemplateAdmin, b: PromptTemplateAdmin): boolean {
  return (
    a.key === b.key &&
    a.module === b.module &&
    a.submodule === b.submodule &&
    a.mode === b.mode &&
    a.language === b.language &&
    String(a.grade ?? '') === String(b.grade ?? '')
  );
}

function sameBlockScope(a: PromptBlockAdmin, b: PromptBlockAdmin): boolean {
  return (
    a.key === b.key &&
    a.block_type === b.block_type &&
    a.module === b.module &&
    a.submodule === b.submodule
  );
}

function formatBlockSelectLabel(b: PromptBlockAdmin): string {
  const n = (b.name || '').trim() || b.key;
  const inactive = b.status !== 'active' ? ' — inactive' : '';
  return `${n} (${b.key})${inactive}`;
}

function formatDialectSelectLabel(d: DialectProfileAdmin): string {
  const n = (d.name || '').trim() || d.key;
  const inactive = d.status !== 'active' ? ' — inactive' : '';
  return `${n} (${d.key})${inactive}`;
}

const BINDING_SELECT_CLASS =
  'w-full rounded-lg border border-app-border dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-app-text dark:text-gray-100';

export const PromptTemplatePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ActiveTab>('template');
  const [templates, setTemplates] = useState<PromptTemplateAdmin[]>([]);
  const [blocks, setBlocks] = useState<PromptBlockAdmin[]>([]);
  const [dialects, setDialects] = useState<DialectProfileAdmin[]>([]);
  const [bindings, setBindings] = useState<PromptBindingAdmin[]>([]);
  const [schools, setSchools] = useState<School[]>([]);
  const [schoolsLoading, setSchoolsLoading] = useState(false);
  const [schoolsError, setSchoolsError] = useState<string | null>(null);
  const [blockSearch, setBlockSearch] = useState('');
  const [blockTypeFilter, setBlockTypeFilter] = useState('');
  const [blockStatusFilter, setBlockStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const [editing, setEditing] = useState<PromptTemplateAdmin | null>(null);
  const [editName, setEditName] = useState('');
  const [editTemplate, setEditTemplate] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const [editingBlock, setEditingBlock] = useState<PromptBlockAdmin | null>(null);
  const [editBlockName, setEditBlockName] = useState('');
  const [editBlockContent, setEditBlockContent] = useState('');
  const [savingBlock, setSavingBlock] = useState(false);

  const [editingDialect, setEditingDialect] = useState<DialectProfileAdmin | null>(null);
  const [editDialectName, setEditDialectName] = useState('');
  const [editDialectDescription, setEditDialectDescription] = useState('');
  const [editDialectInstruction, setEditDialectInstruction] = useState('');
  const [savingDialect, setSavingDialect] = useState(false);

  const [historyTarget, setHistoryTarget] = useState<PromptTemplateAdmin | null>(null);
  const [historyBlockTarget, setHistoryBlockTarget] = useState<PromptBlockAdmin | null>(null);
  const [historyDialectTarget, setHistoryDialectTarget] = useState<DialectProfileAdmin | null>(null);

  const [confirmTemplate, setConfirmTemplate] = useState<PromptTemplateAdmin | null>(null);
  const [confirmBlock, setConfirmBlock] = useState<PromptBlockAdmin | null>(null);
  const [confirmDialect, setConfirmDialect] = useState<DialectProfileAdmin | null>(null);

  const [deleteTemplateTarget, setDeleteTemplateTarget] = useState<PromptTemplateAdmin | null>(null);
  const [deleteBlockTarget, setDeleteBlockTarget] = useState<PromptBlockAdmin | null>(null);
  const [deleteDialectTarget, setDeleteDialectTarget] = useState<DialectProfileAdmin | null>(null);
  const [savingDelete, setSavingDelete] = useState(false);

  const [createTemplateOpen, setCreateTemplateOpen] = useState(false);
  const [ctKey, setCtKey] = useState('');
  const [ctName, setCtName] = useState('');
  const [ctModule, setCtModule] = useState(DEFAULT_AI_TUTOR_MODULE);
  const [ctSubmodule, setCtSubmodule] = useState('lesson_tutor');
  const [ctMode, setCtMode] = useState<(typeof MODES)[number]>('FREE');
  const [ctGrade, setCtGrade] = useState('2');
  const [ctLanguage, setCtLanguage] = useState('ta');
  const [ctBody, setCtBody] = useState('');
  const [savingCreateTemplate, setSavingCreateTemplate] = useState(false);
  const [ctAutoBinding, setCtAutoBinding] = useState(false);
  const [ctAllowGlobal, setCtAllowGlobal] = useState(false);
  const [ctTenantId, setCtTenantId] = useState('');
  const [ctBindingPriority, setCtBindingPriority] = useState('1');
  const [ctAllGradesWildcard, setCtAllGradesWildcard] = useState(false);
  const [ctBaseRulesKey, setCtBaseRulesKey] = useState('');
  const [ctLessonTypeRulesKey, setCtLessonTypeRulesKey] = useState('');
  const [ctModeRulesKey, setCtModeRulesKey] = useState('');
  const [ctSourceRulesKey, setCtSourceRulesKey] = useState('');
  const [ctFinalOutputKey, setCtFinalOutputKey] = useState('');
  const [ctCommRulesKey, setCtCommRulesKey] = useState('');
  const [ctDialectBinding, setCtDialectBinding] = useState('');
  const [ctOverwriteBinding, setCtOverwriteBinding] = useState(false);
  const [ctBindingFormError, setCtBindingFormError] = useState('');
  const [ctLessonTypeRuleSlots, setCtLessonTypeRuleSlots] = useState(emptyLessonTypeRuleSlots);
  const [ctShowDynamicLessonMap, setCtShowDynamicLessonMap] = useState(false);

  // Template type switch for AI Tutor vs Picture Talk (as requested)
  const [templateType, setTemplateType] = useState<'ai_tutor' | 'picture_talk'>('ai_tutor');

  // Listing filter for Template type swap (AI Tutor / Picture Talk / All)
  const [templateListFilter, setTemplateListFilter] = useState<'all' | 'ai_tutor' | 'picture_talk'>('all');

  // Picture Talk specific create form state
  const [ctPictureTalkSubmodule, setCtPictureTalkSubmodule] = useState<(typeof PICTURE_TALK_SUBMODULES)[number]>('conversation');
  const [ctEventType, setCtEventType] = useState<(typeof PICTURE_TALK_EVENT_TYPES)[number]>('start');
  const [ctPictureTalkRulesKey, setCtPictureTalkRulesKey] = useState('');
  const [ctDialectRulesKey, setCtDialectRulesKey] = useState('');
  const [ctEventRulesKey, setCtEventRulesKey] = useState('');
  const [ctSafetyRulesKey, setCtSafetyRulesKey] = useState('');

  /** Edit template: optional auto-binding (same API as create). */
  const [etAutoBinding, setEtAutoBinding] = useState(false);
  const [etAllowGlobal, setEtAllowGlobal] = useState(false);
  const [etTenantId, setEtTenantId] = useState('');
  const [etBindingPriority, setEtBindingPriority] = useState('1');
  const [etBaseRulesKey, setEtBaseRulesKey] = useState('');
  const [etLessonTypeRulesKey, setEtLessonTypeRulesKey] = useState('');
  const [etModeRulesKey, setEtModeRulesKey] = useState('');
  const [etSourceRulesKey, setEtSourceRulesKey] = useState('');
  const [etFinalOutputKey, setEtFinalOutputKey] = useState('');
  const [etCommRulesKey, setEtCommRulesKey] = useState('');
  const [etDialectBinding, setEtDialectBinding] = useState('');
  const [etOverwriteBinding, setEtOverwriteBinding] = useState(false);
  const [etBindingFormError, setEtBindingFormError] = useState('');
  const [etLessonTypeRuleSlots, setEtLessonTypeRuleSlots] = useState(emptyLessonTypeRuleSlots);
  const [etShowDynamicLessonMap, setEtShowDynamicLessonMap] = useState(false);
  const [etPictureTalkRulesKey, setEtPictureTalkRulesKey] = useState('');
  const [etDialectRulesKey, setEtDialectRulesKey] = useState('');
  const [etEventRulesKey, setEtEventRulesKey] = useState('');
  const [etSafetyRulesKey, setEtSafetyRulesKey] = useState('');

  const [createBlockOpen, setCreateBlockOpen] = useState(false);
  const [cbKey, setCbKey] = useState('');
  const [cbBlockType, setCbBlockType] = useState<(typeof BLOCK_TYPES)[number]>('lesson_type_rules');
  const [cbName, setCbName] = useState('');
  const [cbModule, setCbModule] = useState(DEFAULT_AI_TUTOR_MODULE);
  const [cbSubmodule, setCbSubmodule] = useState('lesson_tutor');
  const [cbContent, setCbContent] = useState('');
  const [savingCreateBlock, setSavingCreateBlock] = useState(false);

  const [activatingId, setActivatingId] = useState<string | null>(null);

  const [createDialectOpen, setCreateDialectOpen] = useState(false);
  const [cdKey, setCdKey] = useState('');
  const [cdName, setCdName] = useState('');
  const [cdLanguage, setCdLanguage] = useState('ta');
  const [cdInstruction, setCdInstruction] = useState('');
  const [cdDescription, setCdDescription] = useState('');
  const [savingCreateDialect, setSavingCreateDialect] = useState(false);

  const fetchPromptLibrary = useCallback(async () => {
    setLoading(true);
    try {
      const [templateRes, blockRes, dialectRes, bindingRes] = await Promise.allSettled([
        getPromptLibraryTemplates(),
        getPromptLibraryBlocks(),
        getPromptLibraryDialects(),
        getPromptLibraryBindings(),
      ]);

      if (templateRes.status === 'fulfilled') {
        setTemplates(templateRes.value.filter((item) => item.type === 'template'));
      } else {
        setTemplates([]);
      }
      if (blockRes.status === 'fulfilled') {
        setBlocks(blockRes.value.filter((item) => item.type === 'block'));
      } else {
        setBlocks([]);
      }
      if (dialectRes.status === 'fulfilled') {
        setDialects(dialectRes.value.filter((item) => item.type === 'dialect'));
      } else {
        setDialects([]);
      }
      if (bindingRes.status === 'fulfilled') {
        setBindings(bindingRes.value);
      } else {
        setBindings([]);
      }

      const failedTabs = [
        templateRes.status === 'rejected' ? 'templates' : null,
        blockRes.status === 'rejected' ? 'blocks' : null,
        dialectRes.status === 'rejected' ? 'dialects' : null,
        bindingRes.status === 'rejected' ? 'bindings' : null,
      ].filter(Boolean);

      if (failedTabs.length > 0) {
        const firstRejected = [templateRes, blockRes, dialectRes, bindingRes].find(
          (r): r is PromiseRejectedResult => r.status === 'rejected'
        );
        const detail = firstRejected ? handleApiError(firstRejected.reason) : '';
        showErrorToast(
          `Failed to load ${failedTabs.join(', ')}. ${
            detail ||
              'Check that the AI Tutor service is running and exposes GET /admin/prompt-library/templates (e.g. http://localhost:8007).'
          }`
        );
      }
    } catch (e: unknown) {
      showErrorToast(e);
      setTemplates([]);
      setBlocks([]);
      setDialects([]);
      setBindings([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchPromptLibrary();
  }, [fetchPromptLibrary]);

  const fetchSchools = useCallback(async () => {
    setSchoolsLoading(true);
    setSchoolsError(null);
    try {
      const data = await listSchools();
      setSchools(data || []);
    } catch (error) {
      const msg = handleApiError(error);
      setSchoolsError(msg);
      showErrorToast(error);
    } finally {
      setSchoolsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchSchools();
  }, [fetchSchools]);

  /** Hide old snake_case seed rows; canonical keys are UPPER_SNAKE (prompt_seed). */
  const visibleBlocks = useMemo(
    () => blocks.filter((b) => !isLegacyPromptBlockKey(b.key)),
    [blocks]
  );
  const legacyBlockHiddenCount = blocks.length - visibleBlocks.length;

  const latestTemplates = useMemo(
    () =>
      pickLatestPerGroup(
        templates,
        (t) => `${t.key}|${t.module}|${t.submodule}|${t.mode}|${t.language}|${t.grade ?? ''}`,
        (t) => t.version,
        (t) => t.status
      ),
    [templates]
  );

  // Filter templates by selected list filter (All / AI Tutor / Picture Talk)
  const filteredTemplates = useMemo(() => {
    if (templateListFilter === 'all') return latestTemplates;
    if (templateListFilter === 'picture_talk') {
      return latestTemplates.filter((t) => t.module === 'picture_talk');
    }
    return latestTemplates.filter((t) => t.module !== 'picture_talk');
  }, [latestTemplates, templateListFilter]);

  const aiTutorTemplateCount = useMemo(
    () => latestTemplates.filter((t) => t.module !== 'picture_talk').length,
    [latestTemplates]
  );
  const pictureTalkTemplateCount = useMemo(
    () => latestTemplates.filter((t) => t.module === 'picture_talk').length,
    [latestTemplates]
  );

  const latestBlocks = useMemo(
    () =>
      pickLatestPerGroup(
        visibleBlocks,
        (b) => `${b.key}|${b.block_type}|${b.module}|${b.submodule}`,
        (b) => b.version,
        (b) => b.status
      ),
    [visibleBlocks]
  );

  const filteredBlocks = useMemo(() => {
    let result = latestBlocks;
    const term = blockSearch.toLowerCase().trim();
    if (term) {
      result = result.filter((b) =>
        b.key.toLowerCase().includes(term) ||
        (b.name || '').toLowerCase().includes(term) ||
        b.block_type.toLowerCase().includes(term)
      );
    }
    if (blockTypeFilter) {
      result = result.filter((b) => b.block_type === blockTypeFilter);
    }
    if (blockStatusFilter && blockStatusFilter !== 'all') {
      result = result.filter((b) => b.status === blockStatusFilter);
    }
    return result;
  }, [latestBlocks, blockSearch, blockTypeFilter, blockStatusFilter]);

  const latestDialects = useMemo(
    () => pickLatestPerGroup(dialects, (d) => d.key, (d) => d.version, (d) => d.status),
    [dialects]
  );

  const blocksForCreateBinding = useMemo(() => {
    if (templateType === 'picture_talk') {
      return latestBlocks.filter(
        (b) =>
          b.module === 'picture_talk' ||
          (b.block_type === 'base_rules' ||
            b.block_type === 'final_output_check' ||
            b.block_type === 'source_rules')
      );
    }
    const effectiveModule = ctModule.trim();
    const effectiveSubmodule = ctSubmodule.trim();
    return latestBlocks.filter(
      (b) => b.module === effectiveModule && b.submodule === effectiveSubmodule
    );
  }, [latestBlocks, templateType, ctModule, ctSubmodule]);

  const blocksForEditBinding = useMemo(() => {
    if (!editing) return [];
    if (editing.module === 'picture_talk') {
      return latestBlocks.filter(
        (b) =>
          b.module === 'picture_talk' ||
          (b.block_type === 'base_rules' || b.block_type === 'final_output_check' || b.block_type === 'source_rules')
      );
    }
    return latestBlocks.filter((b) => b.module === editing.module && b.submodule === editing.submodule);
  }, [latestBlocks, editing]);

  const createBindingUiSplit = useMemo(() => {
    if (templateType === 'picture_talk') {
      return {
        showLessonSlots: false,
        showCommSlot: false,
        emphasizeComm: false,
        isPictureTalk: true,
      };
    }
    const s = ctSubmodule.trim();
    return {
      showLessonSlots: s !== 'communication_tutor',
      showCommSlot: s === 'communication_tutor' || s === 'lesson_tutor',
      emphasizeComm: s === 'communication_tutor',
      isPictureTalk: false,
    };
  }, [templateType, ctSubmodule]);

  const editBindingUiSplit = useMemo(() => {
    if (!editing) {
      return { showLessonSlots: true, showCommSlot: true, emphasizeComm: false, isPictureTalk: false };
    }
    if (editing.module === 'picture_talk') {
      return { showLessonSlots: false, showCommSlot: false, emphasizeComm: false, isPictureTalk: true };
    }
    const s = editing.submodule.trim();
    return {
      showLessonSlots: s !== 'communication_tutor',
      showCommSlot: s === 'communication_tutor' || s === 'lesson_tutor',
      emphasizeComm: s === 'communication_tutor',
      isPictureTalk: false,
    };
  }, [editing]);

  const totalCount = useMemo(
    () => latestTemplates.length + latestBlocks.length + latestDialects.length,
    [latestTemplates.length, latestBlocks.length, latestDialects.length]
  );

  const historyRows = useMemo(() => {
    if (!historyTarget) return [];
    return templates
      .filter(
        (t) =>
          t.key === historyTarget.key &&
          t.module === historyTarget.module &&
          t.submodule === historyTarget.submodule &&
          t.mode === historyTarget.mode &&
          t.language === historyTarget.language &&
          String(t.grade ?? '') === String(historyTarget.grade ?? '')
      )
      .sort((a, b) => b.version - a.version);
  }, [historyTarget, templates]);

  const historyBlockRows = useMemo(() => {
    if (!historyBlockTarget) return [];
    return visibleBlocks
      .filter(
        (b) =>
          b.key === historyBlockTarget.key &&
          b.block_type === historyBlockTarget.block_type &&
          b.module === historyBlockTarget.module &&
          b.submodule === historyBlockTarget.submodule
      )
      .sort((a, b) => b.version - a.version);
  }, [historyBlockTarget, visibleBlocks]);

  const historyDialectRows = useMemo(() => {
    if (!historyDialectTarget) return [];
    return dialects.filter((d) => d.key === historyDialectTarget.key).sort((a, b) => b.version - a.version);
  }, [historyDialectTarget, dialects]);

  const historyTemplateRuntimeId = useMemo(() => pickRuntimeWinnerId(historyRows), [historyRows]);
  const historyBlockRuntimeId = useMemo(() => pickRuntimeWinnerId(historyBlockRows), [historyBlockRows]);
  const historyDialectRuntimeId = useMemo(() => pickRuntimeWinnerId(historyDialectRows), [historyDialectRows]);

  const findBindingForTemplate = (row: PromptTemplateAdmin): PromptBindingAdmin | null => {
    if (!row.key || !bindings.length) return null;
    const exact = bindings.find(
      (b) =>
        b.prompt_template_key === row.key &&
        b.module === row.module &&
        b.submodule === row.submodule &&
        b.mode === row.mode &&
        String(b.grade ?? '') === String(row.grade ?? '') &&
        b.language === row.language
    );
    if (exact) return exact;

    // Picture Talk templates are usually global (grade=null) but their bindings
    // target specific grades. Fall back to the first binding that points at this
    // template_key under the same module so the auto-binding form prefills.
    if (row.module === 'picture_talk') {
      return (
        bindings.find(
          (b) => b.prompt_template_key === row.key && b.module === row.module
        ) || null
      );
    }
    return null;
  };

  const openEdit = (row: PromptTemplateAdmin) => {
    setEditing(row);
    setEditName(row.name);
    setEditTemplate(row.template ?? '');

    const matching = findBindingForTemplate(row);
    if (matching) {
      setEtAutoBinding(true);
      setEtAllowGlobal(!matching.tenant_id);
      setEtTenantId(matching.tenant_id || '');
      setEtBindingPriority(String(matching.priority || 1));
      setEtDialectBinding(matching.default_dialect_key || '');
      const bm = matching.block_mappings || {};
      setEtBaseRulesKey(bm.base_rules || bm['base_rules'] || '');
      setEtLessonTypeRulesKey(bm.lesson_type_rules || bm['lesson_type_rules'] || '');
      setEtModeRulesKey(bm.mode_rules || bm['mode_rules'] || '');
      setEtSourceRulesKey(bm.source_rules || bm['source_rules'] || '');
      setEtFinalOutputKey(bm.final_output_check || bm['final_output_check'] || '');
      setEtCommRulesKey(bm.communication_rules || bm['communication_rules'] || '');
      setEtPictureTalkRulesKey((bm as any).picture_talk_rules || (bm as any)['picture_talk_rules'] || '');
      // For Picture Talk, do not load/set dialect_rules_key from block_mappings (use default_dialect only; clears stale EELAM)
      setEtDialectRulesKey(row.module === 'picture_talk' ? '' : ((bm as any).dialect_rules || (bm as any)['dialect_rules'] || ''));
      setEtEventRulesKey((bm as any).event_rules || (bm as any)['event_rules'] || '');
      setEtSafetyRulesKey((bm as any).safety_rules || (bm as any)['safety_rules'] || '');
      setEtOverwriteBinding(false);
      setEtBindingFormError('');
      setEtLessonTypeRuleSlots(emptyLessonTypeRuleSlots());
      setEtShowDynamicLessonMap(!!matching.lesson_type_rule_map && Object.keys(matching.lesson_type_rule_map).length > 0);
    } else {
      setEtAutoBinding(false);
      setEtAllowGlobal(false);
      setEtTenantId('');
      setEtBindingPriority('1');
      setEtBaseRulesKey('');
      setEtLessonTypeRulesKey('');
      setEtModeRulesKey('');
      setEtSourceRulesKey('');
      setEtFinalOutputKey('');
      setEtCommRulesKey('');
      setEtPictureTalkRulesKey('');
      setEtDialectRulesKey('');
      setEtEventRulesKey('');
      setEtSafetyRulesKey('');
      setEtDialectBinding('');
      setEtOverwriteBinding(false);
      setEtBindingFormError('');
      setEtLessonTypeRuleSlots(emptyLessonTypeRuleSlots());
      setEtShowDynamicLessonMap(false);
    }
  };

  const openEditBlock = (row: PromptBlockAdmin) => {
    setEditingBlock(row);
    setEditBlockName(row.name);
    setEditBlockContent(row.content ?? '');
  };

  const openEditDialect = (row: DialectProfileAdmin) => {
    setEditingDialect(row);
    setEditDialectName(row.name);
    setEditDialectDescription(row.description ?? '');
    setEditDialectInstruction(row.instruction_block ?? '');
  };

  const runEditSave = async () => {
    if (!editing) return;
    if (etAutoBinding) {
      const err = validateAutoBindingForm({
        tenantId: etTenantId,
        allowGlobalBinding: etAllowGlobal,
        bindingPriority: etBindingPriority,
      });
      if (err) {
        setEtBindingFormError(err);
        return;
      }
      setEtBindingFormError('');
    } else {
      setEtBindingFormError('');
    }
    setSavingEdit(true);
    try {
      const payload: Parameters<typeof updatePromptLibraryTemplate>[1] = {
        name: editName.trim(),
        template: editTemplate,
        updated_by: 'devteam_ui',
      };
      if (etAutoBinding) {
        payload.auto_create_binding = true;
        if (etAllowGlobal) payload.allow_global_binding = true;
        if (etTenantId.trim()) payload.tenant_id = etTenantId.trim();
        const pri = Number(etBindingPriority);
        if (Number.isFinite(pri)) payload.binding_priority = pri;

        // Explicitly send null when "None" is selected so backend clears the field/slot
        if (etBaseRulesKey.trim()) {
          payload.base_rules_key = etBaseRulesKey.trim();
        } else if (etBaseRulesKey === '') {
          (payload as any).base_rules_key = null;
        }
        if (etLessonTypeRulesKey.trim()) {
          payload.lesson_type_rules_key = etLessonTypeRulesKey.trim();
        } else if (etLessonTypeRulesKey === '') {
          (payload as any).lesson_type_rules_key = null;
        }
        if (etModeRulesKey.trim()) {
          payload.mode_rules_key = etModeRulesKey.trim();
        } else if (etModeRulesKey === '') {
          (payload as any).mode_rules_key = null;
        }
        if (etSourceRulesKey.trim()) {
          payload.source_rules_key = etSourceRulesKey.trim();
        } else if (etSourceRulesKey === '') {
          (payload as any).source_rules_key = null;
        }
        if (etFinalOutputKey.trim()) {
          payload.final_output_check_key = etFinalOutputKey.trim();
        } else if (etFinalOutputKey === '') {
          (payload as any).final_output_check_key = null;
        }
        if (etCommRulesKey.trim()) {
          payload.communication_rules_key = etCommRulesKey.trim();
        } else if (etCommRulesKey === '') {
          (payload as any).communication_rules_key = null;
        }
        if (etPictureTalkRulesKey.trim()) {
          (payload as any).picture_talk_rules_key = etPictureTalkRulesKey.trim();
        } else if (etPictureTalkRulesKey === '') {
          (payload as any).picture_talk_rules_key = null;
        }
        // Skip dialect_rules_key entirely for Picture Talk (controlled by default_dialect_key only; clears any stale value from DB)
        if (editing?.module !== 'picture_talk') {
          if (etDialectRulesKey.trim()) {
            (payload as any).dialect_rules_key = etDialectRulesKey.trim();
          } else if (etDialectRulesKey === '') {
            (payload as any).dialect_rules_key = null;
          }
        } else if (etDialectRulesKey === '' || editing?.module === 'picture_talk') {
          // Force clear dialect_rules_key from block_mappings for PT to prevent stale EELAM
          (payload as any).dialect_rules_key = null;
        }
        if (etEventRulesKey.trim()) {
          (payload as any).event_rules_key = etEventRulesKey.trim();
        } else if (etEventRulesKey === '') {
          (payload as any).event_rules_key = null;
        }
        if (etSafetyRulesKey.trim()) {
          (payload as any).safety_rules_key = etSafetyRulesKey.trim();
        } else if (etSafetyRulesKey === '') {
          (payload as any).safety_rules_key = null;
        }
        if (etDialectBinding.trim()) {
          payload.default_dialect_key = etDialectBinding.trim();
        } else if (etDialectBinding === '') {
          (payload as any).default_dialect_key = null;
        }

        // Force overwrite when clearing any slot OR when editing a Picture Talk template
        // (ensures updated blocks/dialect immediately reflected in runtime; clears old values)
        const clearingAnySlot =
          etBaseRulesKey === '' ||
          etLessonTypeRulesKey === '' ||
          etModeRulesKey === '' ||
          etSourceRulesKey === '' ||
          etFinalOutputKey === '' ||
          etCommRulesKey === '' ||
          etPictureTalkRulesKey === '' ||
          etEventRulesKey === '' ||
          etSafetyRulesKey === '' ||
          etDialectBinding === '' ||
          (editing?.module === 'picture_talk' && etDialectRulesKey === '');  // include for PT clear

        if (clearingAnySlot || etOverwriteBinding || editing?.module === 'picture_talk') {
          payload.overwrite_existing_block_mappings = true;
        }

        if (etShowDynamicLessonMap) {
          const dyn = buildLessonTypeRuleMapFromSlots(etLessonTypeRuleSlots);
          payload.lesson_type_rule_map = dyn ?? {};
        } else {
          payload.lesson_type_rule_map = {};
        }
      }
      const updated = await updatePromptLibraryTemplate(editing.id, payload);
      showSuccessToast(messageForBindingOutcome('template_updated', updated.binding_status, updated.binding_reason));
      setEditing(null);
      await fetchPromptLibrary();
    } catch (e) {
      showErrorToast(e);
    } finally {
      setSavingEdit(false);
    }
  };

  const runEditBlockSave = async () => {
    if (!editingBlock) return;
    setSavingBlock(true);
    try {
      await updatePromptLibraryBlock(editingBlock.id, {
        name: editBlockName.trim(),
        content: editBlockContent,
        updated_by: 'devteam_ui',
      });
      showSuccessToast('Block updated');
      setEditingBlock(null);
      await fetchPromptLibrary();
    } catch (e) {
      showErrorToast(e);
    } finally {
      setSavingBlock(false);
    }
  };

  const runEditDialectSave = async () => {
    if (!editingDialect) return;
    setSavingDialect(true);
    try {
      await updateDialectProfileById(editingDialect.id, {
        name: editDialectName.trim(),
        description: editDialectDescription.trim(),
        instruction_block: editDialectInstruction,
        updated_by: 'devteam_ui',
      });
      showSuccessToast('Dialect updated');
      setEditingDialect(null);
      await fetchPromptLibrary();
    } catch (e) {
      showErrorToast(e);
    } finally {
      setSavingDialect(false);
    }
  };

  const runToggleTemplate = async () => {
    if (!confirmTemplate) return;
    try {
      await updatePromptLibraryTemplate(confirmTemplate.id, {
        status: confirmTemplate.status === 'active' ? 'inactive' : 'active',
        updated_by: 'devteam_ui',
      });
      showSuccessToast(confirmTemplate.status === 'active' ? 'Template deactivated' : 'Template activated');
      setConfirmTemplate(null);
      await fetchPromptLibrary();
    } catch (e) {
      showErrorToast(e);
    }
  };

  const runToggleBlock = async () => {
    if (!confirmBlock) return;
    try {
      await updatePromptLibraryBlock(confirmBlock.id, {
        status: confirmBlock.status === 'active' ? 'inactive' : 'active',
        updated_by: 'devteam_ui',
      });
      showSuccessToast(confirmBlock.status === 'active' ? 'Block deactivated' : 'Block activated');
      setConfirmBlock(null);
      await fetchPromptLibrary();
    } catch (e) {
      showErrorToast(e);
    }
  };

  const runToggleDialect = async () => {
    if (!confirmDialect) return;
    try {
      await updateDialectProfileById(confirmDialect.id, {
        status: confirmDialect.status === 'active' ? 'inactive' : 'active',
        updated_by: 'devteam_ui',
      });
      showSuccessToast(confirmDialect.status === 'active' ? 'Dialect deactivated' : 'Dialect activated');
      setConfirmDialect(null);
      await fetchPromptLibrary();
    } catch (e) {
      showErrorToast(e);
    }
  };

  const runDeleteTemplate = async () => {
    if (!deleteTemplateTarget) return;
    setSavingDelete(true);
    try {
      await deletePromptLibraryTemplate(deleteTemplateTarget.id);
      showSuccessToast('Template deleted');
      if (editing && sameTemplateScope(editing, deleteTemplateTarget)) setEditing(null);
      if (historyTarget && sameTemplateScope(historyTarget, deleteTemplateTarget)) setHistoryTarget(null);
      setDeleteTemplateTarget(null);
      await fetchPromptLibrary();
    } catch (e) {
      showErrorToast(e);
    } finally {
      setSavingDelete(false);
    }
  };

  const runDeleteBlock = async () => {
    if (!deleteBlockTarget) return;
    setSavingDelete(true);
    try {
      await deletePromptLibraryBlock(deleteBlockTarget.id);
      showSuccessToast('Block deleted');
      if (editingBlock && sameBlockScope(editingBlock, deleteBlockTarget)) setEditingBlock(null);
      if (historyBlockTarget && sameBlockScope(historyBlockTarget, deleteBlockTarget)) setHistoryBlockTarget(null);
      setDeleteBlockTarget(null);
      await fetchPromptLibrary();
    } catch (e) {
      showErrorToast(e);
    } finally {
      setSavingDelete(false);
    }
  };

  const runDeleteDialect = async () => {
    if (!deleteDialectTarget) return;
    setSavingDelete(true);
    try {
      await deleteDialectProfileGroupById(deleteDialectTarget.id);
      showSuccessToast('Dialect profile deleted');
      if (editingDialect && editingDialect.key === deleteDialectTarget.key) setEditingDialect(null);
      if (historyDialectTarget && historyDialectTarget.key === deleteDialectTarget.key) setHistoryDialectTarget(null);
      setDeleteDialectTarget(null);
      await fetchPromptLibrary();
    } catch (e) {
      showErrorToast(e);
    } finally {
      setSavingDelete(false);
    }
  };

  const runActivateTemplateVersion = async (id: string) => {
    setActivatingId(id);
    try {
      await activatePromptTemplateVersion(id);
      showSuccessToast('This version is now active for runtime');
      await fetchPromptLibrary();
    } catch (e) {
      showErrorToast(e);
    } finally {
      setActivatingId(null);
    }
  };

  const runActivateBlockVersion = async (id: string) => {
    setActivatingId(id);
    try {
      await activatePromptBlockVersion(id);
      showSuccessToast('This version is now active for runtime');
      await fetchPromptLibrary();
    } catch (e) {
      showErrorToast(e);
    } finally {
      setActivatingId(null);
    }
  };

  const runActivateDialectVersion = async (id: string) => {
    setActivatingId(id);
    try {
      await activateDialectProfileVersion(id);
      showSuccessToast('This version is now active for runtime');
      await fetchPromptLibrary();
    } catch (e) {
      showErrorToast(e);
    } finally {
      setActivatingId(null);
    }
  };

  const runCreateTemplate = async () => {
    if (ctAutoBinding) {
      const err = validateAutoBindingForm({
        tenantId: ctTenantId,
        allowGlobalBinding: ctAllowGlobal,
        bindingPriority: ctBindingPriority,
      });
      if (err) {
        setCtBindingFormError(err);
        return;
      }
      setCtBindingFormError('');
    } else {
      setCtBindingFormError('');
    }
    setSavingCreateTemplate(true);
    try {
      const gradeNum = ctGrade.trim() === '' ? undefined : Number(ctGrade);
      const isPictureTalk = templateType === 'picture_talk';

      const payload: Parameters<typeof createPromptLibraryTemplate>[0] = {
        key: ctKey.trim(),
        name: ctName.trim(),
        module: isPictureTalk ? 'picture_talk' : ctModule.trim() || DEFAULT_AI_TUTOR_MODULE,
        submodule: isPictureTalk ? ctPictureTalkSubmodule : ctSubmodule.trim(),
        mode: (isPictureTalk ? 'LIVE' : ctMode) as any,
        language: ctLanguage.trim(),
        template: ctBody,
        variables: [],
        supported_lesson_types: [],
        supported_dialects: [],
        description: isPictureTalk ? `Picture Talk - ${ctEventType}` : '',
        tags: ['devteam_ui', isPictureTalk ? 'picture_talk' : 'ai_tutor'],
        created_by: 'devteam_ui',
      };
      if (ctAllGradesWildcard) {
        payload.grade = null;
      } else if (gradeNum !== undefined && Number.isFinite(gradeNum)) {
        payload.grade = gradeNum;
      }
      if (ctAutoBinding) {
        payload.auto_create_binding = true;
        if (ctAllowGlobal) payload.allow_global_binding = true;
        if (ctTenantId.trim()) payload.tenant_id = ctTenantId.trim();
        const pri = Number(ctBindingPriority);
        if (Number.isFinite(pri)) payload.binding_priority = pri;
        if (ctBaseRulesKey.trim()) {
          payload.base_rules_key = ctBaseRulesKey.trim();
        } else if (ctBaseRulesKey === '') {
          (payload as any).base_rules_key = null;
        }
        if (ctLessonTypeRulesKey.trim()) {
          payload.lesson_type_rules_key = ctLessonTypeRulesKey.trim();
        } else if (ctLessonTypeRulesKey === '') {
          (payload as any).lesson_type_rules_key = null;
        }
        if (ctModeRulesKey.trim()) {
          payload.mode_rules_key = ctModeRulesKey.trim();
        } else if (ctModeRulesKey === '') {
          (payload as any).mode_rules_key = null;
        }
        if (ctSourceRulesKey.trim()) {
          payload.source_rules_key = ctSourceRulesKey.trim();
        } else if (ctSourceRulesKey === '') {
          (payload as any).source_rules_key = null;
        }
        if (ctFinalOutputKey.trim()) {
          payload.final_output_check_key = ctFinalOutputKey.trim();
        } else if (ctFinalOutputKey === '') {
          (payload as any).final_output_check_key = null;
        }
        if (ctCommRulesKey.trim()) {
          payload.communication_rules_key = ctCommRulesKey.trim();
        } else if (ctCommRulesKey === '') {
          (payload as any).communication_rules_key = null;
        }
        if (ctDialectBinding.trim()) {
          payload.default_dialect_key = ctDialectBinding.trim();
        } else if (ctDialectBinding === '') {
          (payload as any).default_dialect_key = null;
        }

        // Picture Talk specific binding keys (req 5, 6). No dialect_rules_key for PT - Default dialect controls injection.
        if (isPictureTalk) {
          if (ctPictureTalkRulesKey.trim()) {
            (payload as any).picture_talk_rules_key = ctPictureTalkRulesKey.trim();
          }
          // Do not set dialect_rules_key for Picture Talk (cleared via null in edit; avoids stale block_mappings)
          if (ctEventRulesKey.trim()) {
            (payload as any).event_rules_key = ctEventRulesKey.trim();
          }
          if (ctSafetyRulesKey.trim()) {
            (payload as any).safety_rules_key = ctSafetyRulesKey.trim();
          } else if (ctSafetyRulesKey === '') {
            (payload as any).safety_rules_key = null;
          }
          // Set event context in description for easy identification
          payload.description = `Picture Talk - ${ctEventType} (${ctPictureTalkSubmodule})`;
        } else if (ctDialectRulesKey.trim()) {
          (payload as any).dialect_rules_key = ctDialectRulesKey.trim();
        } else if (ctDialectRulesKey === '') {
          (payload as any).dialect_rules_key = null;
        }

        const clearingAnySlot =
          ctBaseRulesKey === '' ||
          ctLessonTypeRulesKey === '' ||
          ctModeRulesKey === '' ||
          ctSourceRulesKey === '' ||
          ctFinalOutputKey === '' ||
          ctCommRulesKey === '' ||
          ctDialectBinding === '' ||
          (isPictureTalk && (ctPictureTalkRulesKey === '' || ctEventRulesKey === '' || ctSafetyRulesKey === '')) ||
          (!isPictureTalk && ctDialectRulesKey === '');

        if (clearingAnySlot || ctOverwriteBinding) {
          payload.overwrite_existing_block_mappings = true;
        }

        if (ctShowDynamicLessonMap && !isPictureTalk) {
          const dyn = buildLessonTypeRuleMapFromSlots(ctLessonTypeRuleSlots);
          payload.lesson_type_rule_map = dyn ?? {};
        } else {
          payload.lesson_type_rule_map = {};
        }
      }
      const created = await createPromptLibraryTemplate(payload);
      showSuccessToast(messageForBindingOutcome('template_created', created.binding_status, created.binding_reason));
      setCreateTemplateOpen(false);
      setCtKey('');
      setCtName('');
      setCtBody('');
      setCtAutoBinding(false);
      setCtAllowGlobal(false);
      setCtTenantId('');
      setCtBindingPriority('1');
      setCtAllGradesWildcard(false);
      setCtBaseRulesKey('');
      setCtLessonTypeRulesKey('');
      setCtModeRulesKey('');
      setCtSourceRulesKey('');
      setCtFinalOutputKey('');
      setCtCommRulesKey('');
      setCtDialectBinding('');
      setCtOverwriteBinding(false);
      setCtLessonTypeRuleSlots(emptyLessonTypeRuleSlots());
      setCtShowDynamicLessonMap(false);
      // Reset Picture Talk states
      setTemplateType('ai_tutor');
      setCtPictureTalkSubmodule('conversation');
      setCtEventType('start');
      setCtPictureTalkRulesKey('');
      setCtDialectRulesKey('');
      setCtEventRulesKey('');
      setCtSafetyRulesKey('');
      await fetchPromptLibrary();
    } catch (e) {
      showErrorToast(e);
    } finally {
      setSavingCreateTemplate(false);
    }
  };

  const runCreateBlock = async () => {
    setSavingCreateBlock(true);
    try {
      await createPromptLibraryBlock({
        key: cbKey.trim(),
        block_type: cbBlockType,
        name: cbName.trim(),
        module: cbModule.trim() || DEFAULT_AI_TUTOR_MODULE,
        submodule: cbSubmodule.trim(),
        content: cbContent,
        tags: ['devteam_ui'],
        created_by: 'devteam_ui',
      });
      showSuccessToast('Block created');
      setCreateBlockOpen(false);
      setCbKey('');
      setCbName('');
      setCbContent('');
      setCbModule(DEFAULT_AI_TUTOR_MODULE);
      await fetchPromptLibrary();
    } catch (e) {
      showErrorToast(e);
    } finally {
      setSavingCreateBlock(false);
    }
  };

  const runCreateDialect = async () => {
    setSavingCreateDialect(true);
    try {
      await createDialectProfile({
        key: cdKey.trim(),
        name: cdName.trim(),
        language: cdLanguage.trim(),
        description: cdDescription.trim(),
        instruction_block: cdInstruction,
        tags: ['devteam_ui'],
        created_by: 'devteam_ui',
      });
      showSuccessToast('Dialect profile created');
      setCreateDialectOpen(false);
      setCdKey('');
      setCdName('');
      setCdInstruction('');
      setCdDescription('');
      await fetchPromptLibrary();
    } catch (e) {
      showErrorToast(e);
    } finally {
      setSavingCreateDialect(false);
    }
  };

  const openAddForTab = () => {
    if (activeTab === 'template') {
      setCtLessonTypeRuleSlots(emptyLessonTypeRuleSlots());
      setCtShowDynamicLessonMap(false);
      setCreateTemplateOpen(true);
    }
    if (activeTab === 'block') {
      setCbModule(DEFAULT_AI_TUTOR_MODULE);
      setCreateBlockOpen(true);
    }
    if (activeTab === 'dialect') setCreateDialectOpen(true);
  };

  return (
    <Layout>
      <PageHeader
        title="Prompt library"
        subtitle="Collection-based Prompt Library from MongoDB. Templates, blocks, and dialect profiles are displayed separately with real fields only."
      />

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <p className="text-sm text-app-text-muted dark:text-gray-400">
          {totalCount} records total ({latestTemplates.length} templates, {latestBlocks.length} blocks
          {legacyBlockHiddenCount > 0 ? `, ${legacyBlockHiddenCount} legacy hidden` : ''},{' '}
          {latestDialects.length} dialects)
        </p>
        <div className="flex flex-wrap gap-2 justify-end">
          <Button type="button" variant="primary" size="sm" onClick={openAddForTab} className="shrink-0">
            <Plus className="w-4 h-4 mr-2" aria-hidden />
            Add {activeTab === 'template' ? 'template' : activeTab === 'block' ? 'block' : 'dialect'}
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={() => void fetchPromptLibrary()} className="shrink-0" disabled={loading}>
            <RefreshCw className="w-4 h-4 mr-2" aria-hidden />
            Refresh
          </Button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {(Object.keys(TAB_LABELS) as ActiveTab[]).map((tabKey) => (
          <Button
            key={tabKey}
            type="button"
            size="sm"
            variant={activeTab === tabKey ? 'primary' : 'outline'}
            onClick={() => setActiveTab(tabKey)}
          >
            {TAB_LABELS[tabKey]}
          </Button>
        ))}
      </div>

      {/* Template Type Listing Filter (AI Tutor / Picture Talk swap) */}
      {activeTab === 'template' && (
        <div className="mb-4 p-3 bg-app-bg/50 dark:bg-gray-900/50 border border-app-border dark:border-gray-700 rounded-xl flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-app-text dark:text-gray-200">Show:</span>
          <div className="inline-flex rounded-lg border border-app-border dark:border-gray-700 p-1 bg-app-bg dark:bg-gray-900">
            <Button
              type="button"
              size="sm"
              variant={templateListFilter === 'all' ? 'primary' : 'outline'}
              onClick={() => setTemplateListFilter('all')}
              className="rounded-md px-4"
            >
              All ({latestTemplates.length})
            </Button>
            <Button
              type="button"
              size="sm"
              variant={templateListFilter === 'ai_tutor' ? 'primary' : 'outline'}
              onClick={() => setTemplateListFilter('ai_tutor')}
              className="rounded-md px-4"
            >
              AI Tutor ({aiTutorTemplateCount})
            </Button>
            <Button
              type="button"
              size="sm"
              variant={templateListFilter === 'picture_talk' ? 'primary' : 'outline'}
              onClick={() => setTemplateListFilter('picture_talk')}
              className="rounded-md px-4"
            >
              Picture Talk ({pictureTalkTemplateCount})
            </Button>
          </div>
        </div>
      )}

      {activeTab === 'block' && (
        <div className="mb-6 p-4 bg-app-bg/50 dark:bg-gray-900/50 border border-app-border dark:border-gray-700 rounded-xl flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[240px]">
            <Input
              placeholder="Search by block key, name, or type..."
              value={blockSearch}
              onChange={(e) => setBlockSearch(e.target.value)}
            />
          </div>
          <div className="min-w-[160px]">
            <label className="block text-xs font-medium text-app-text-muted mb-1.5">Block Type</label>
            <select
              value={blockTypeFilter}
              onChange={(e) => setBlockTypeFilter(e.target.value)}
              className={BINDING_SELECT_CLASS}
            >
              <option value="">All Types</option>
              {BLOCK_TYPES.map((type) => (
                <option key={type} value={type}>
                  {BLOCK_TYPE_LABELS[type as keyof typeof BLOCK_TYPE_LABELS] || type}
                </option>
              ))}
            </select>
          </div>
          <div className="min-w-[120px]">
            <label className="block text-xs font-medium text-app-text-muted mb-1.5">Status</label>
            <select
              value={blockStatusFilter}
              onChange={(e) => setBlockStatusFilter(e.target.value)}
              className={BINDING_SELECT_CLASS}
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setBlockSearch('');
              setBlockTypeFilter('');
              setBlockStatusFilter('all');
            }}
          >
            Clear Filters
          </Button>
          <div className="text-sm text-app-text-muted ml-auto self-end">
            Showing {filteredBlocks.length} of {latestBlocks.length} blocks
          </div>
        </div>
      )}

      <div className="rounded-xl border border-app-border dark:border-gray-700 bg-app-surface dark:bg-gray-900/50 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16">
            <Spinner />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-app-border dark:border-gray-700 bg-app-bg/80 dark:bg-gray-950/50">
                  {activeTab === 'template' && (
                    <>
                      <th className="px-4 py-3 font-semibold text-app-text dark:text-gray-100">Key</th>
                      <th className="px-4 py-3 font-semibold text-app-text dark:text-gray-100">Module</th>
                      <th className="px-4 py-3 font-semibold text-app-text dark:text-gray-100">Mode</th>
                      <th className="px-4 py-3 font-semibold text-app-text dark:text-gray-100">Grade</th>
                      <th className="px-4 py-3 font-semibold text-app-text dark:text-gray-100">Language</th>
                      <th className="px-4 py-3 font-semibold text-app-text dark:text-gray-100">Version</th>
                      <th className="px-4 py-3 font-semibold text-app-text dark:text-gray-100">Status</th>
                      <th className="px-4 py-3 font-semibold text-app-text dark:text-gray-100 text-right">Actions</th>
                    </>
                  )}
                  {activeTab === 'block' && (
                    <>
                      <th className="px-4 py-3 font-semibold text-app-text dark:text-gray-100">Key</th>
                      <th className="px-4 py-3 font-semibold text-app-text dark:text-gray-100">Block Type</th>
                      <th className="px-4 py-3 font-semibold text-app-text dark:text-gray-100">Module</th>
                      <th className="px-4 py-3 font-semibold text-app-text dark:text-gray-100">Submodule</th>
                      <th className="px-4 py-3 font-semibold text-app-text dark:text-gray-100">Version</th>
                      <th className="px-4 py-3 font-semibold text-app-text dark:text-gray-100">Status</th>
                      <th className="px-4 py-3 font-semibold text-app-text dark:text-gray-100 text-right">Actions</th>
                    </>
                  )}
                  {activeTab === 'dialect' && (
                    <>
                      <th className="px-4 py-3 font-semibold text-app-text dark:text-gray-100">Key</th>
                      <th className="px-4 py-3 font-semibold text-app-text dark:text-gray-100">Language</th>
                      <th className="px-4 py-3 font-semibold text-app-text dark:text-gray-100">Name</th>
                      <th className="px-4 py-3 font-semibold text-app-text dark:text-gray-100">Version</th>
                      <th className="px-4 py-3 font-semibold text-app-text dark:text-gray-100">Status</th>
                      <th className="px-4 py-3 font-semibold text-app-text dark:text-gray-100 text-right">Actions</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {activeTab === 'template' &&
                  (filteredTemplates.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-10 text-center text-app-text-muted text-sm">
                        {templateListFilter === 'picture_talk'
                          ? 'No Picture Talk templates yet. Click "Add template" and switch to "Picture Talk Template".'
                          : templateListFilter === 'ai_tutor'
                          ? 'No AI Tutor templates yet. Click "Add template" and use "AI Tutor Template".'
                          : 'No template records yet. Use Add template to create one.'}
                      </td>
                    </tr>
                  ) : (
                    filteredTemplates.map((row) => (
                      <tr
                        key={row.id}
                        className="border-b border-app-border/70 dark:border-gray-800 hover:bg-app-bg/40 dark:hover:bg-gray-800/30"
                      >
                        <td className="px-4 py-3 align-top font-mono text-xs">{row.key}</td>
                        <td className="px-4 py-3 align-top font-mono text-xs">{row.module}</td>
                        <td className="px-4 py-3 align-top font-mono text-xs">{row.mode}</td>
                        <td className="px-4 py-3 align-top font-mono text-xs">{row.grade ?? '—'}</td>
                        <td className="px-4 py-3 align-top font-mono text-xs">{row.language}</td>
                        <td className="px-4 py-3 align-top font-mono text-xs">{row.version}</td>
                        <td className="px-4 py-3 align-top whitespace-nowrap">
                          <Badge variant={row.status === 'active' ? 'success' : 'gray'}>
                            {row.status === 'active' ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 align-top text-right">
                          <div className="flex flex-wrap justify-end gap-1.5">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="!p-2 min-h-[2.25rem] min-w-[2.25rem]"
                              onClick={() => openEdit(row)}
                              aria-label={`Edit template ${row.key}`}
                              title={`Edit · ${row.key}`}
                            >
                              <Pencil className="w-4 h-4 shrink-0" aria-hidden />
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="!p-2 min-h-[2.25rem] min-w-[2.25rem]"
                              onClick={() => setHistoryTarget(row)}
                              aria-label={`Version history · ${row.key}`}
                              title={`History · ${row.key}`}
                            >
                              <History className="w-4 h-4 shrink-0" aria-hidden />
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="!p-2 min-h-[2.25rem] min-w-[2.25rem] text-red-600 border-red-200 hover:bg-red-50 dark:text-red-400 dark:border-red-800 dark:hover:bg-red-950/40"
                              onClick={() => setDeleteTemplateTarget(row)}
                              aria-label={`Delete template ${row.key}`}
                              title="Delete all versions"
                            >
                              <Trash2 className="w-4 h-4 shrink-0" aria-hidden />
                            </Button>
                            <Button
                              type="button"
                              variant={row.status === 'active' ? 'outline' : 'primary'}
                              size="sm"
                              className="!p-2 min-h-[2.25rem] min-w-[2.25rem]"
                              onClick={() => setConfirmTemplate(row)}
                              aria-label={
                                row.status === 'active'
                                  ? `Deactivate template ${row.key}`
                                  : `Activate template ${row.key}`
                              }
                              title={row.status === 'active' ? 'Deactivate' : 'Activate'}
                            >
                              <Power className="w-4 h-4 shrink-0" aria-hidden />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ))}
                {activeTab === 'block' &&
                  (filteredBlocks.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-10 text-center text-app-text-muted text-sm">
                        {blockSearch || blockTypeFilter || blockStatusFilter !== 'all'
                          ? 'No blocks match the current filters.'
                          : blocks.length > 0
                          ? 'Only legacy block rows were returned (hidden). Run scripts/cleanup_legacy_prompt_blocks.py --execute in ai-tutor-service, or add canonical blocks.'
                          : 'No block records yet. Use Add block to create one.'}
                      </td>
                    </tr>
                  ) : (
                    filteredBlocks.map((row) => (
                      <tr
                        key={row.id}
                        className="border-b border-app-border/70 dark:border-gray-800 hover:bg-app-bg/40 dark:hover:bg-gray-800/30"
                      >
                        <td className="px-4 py-3 align-top font-mono text-xs">{row.key}</td>
                        <td className="px-4 py-3 align-top text-xs">{BLOCK_TYPE_LABELS[row.block_type as keyof typeof BLOCK_TYPE_LABELS] || row.block_type}</td>
                        <td className="px-4 py-3 align-top font-mono text-xs">{row.module}</td>
                        <td className="px-4 py-3 align-top font-mono text-xs">{row.submodule}</td>
                        <td className="px-4 py-3 align-top font-mono text-xs">{row.version}</td>
                        <td className="px-4 py-3 align-top whitespace-nowrap">
                          <Badge variant={row.status === 'active' ? 'success' : 'gray'}>
                            {row.status === 'active' ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 align-top text-right">
                          <div className="flex flex-wrap justify-end gap-1.5">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="!p-2 min-h-[2.25rem] min-w-[2.25rem]"
                              onClick={() => openEditBlock(row)}
                              aria-label={`Edit block ${row.key}`}
                              title={`Edit · ${row.key}`}
                            >
                              <Pencil className="w-4 h-4 shrink-0" aria-hidden />
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="!p-2 min-h-[2.25rem] min-w-[2.25rem]"
                              onClick={() => setHistoryBlockTarget(row)}
                              aria-label={`Version history · ${row.key}`}
                              title={`History · ${row.key}`}
                            >
                              <History className="w-4 h-4 shrink-0" aria-hidden />
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="!p-2 min-h-[2.25rem] min-w-[2.25rem] text-red-600 border-red-200 hover:bg-red-50 dark:text-red-400 dark:border-red-800 dark:hover:bg-red-950/40"
                              onClick={() => setDeleteBlockTarget(row)}
                              aria-label={`Delete block ${row.key}`}
                              title="Delete all versions"
                            >
                              <Trash2 className="w-4 h-4 shrink-0" aria-hidden />
                            </Button>
                            <Button
                              type="button"
                              variant={row.status === 'active' ? 'outline' : 'primary'}
                              size="sm"
                              className="!p-2 min-h-[2.25rem] min-w-[2.25rem]"
                              onClick={() => setConfirmBlock(row)}
                              aria-label={
                                row.status === 'active'
                                  ? `Deactivate block ${row.key}`
                                  : `Activate block ${row.key}`
                              }
                              title={row.status === 'active' ? 'Deactivate' : 'Activate'}
                            >
                              <Power className="w-4 h-4 shrink-0" aria-hidden />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ))}
                {activeTab === 'dialect' &&
                  (latestDialects.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-10 text-center text-app-text-muted text-sm">
                        No dialect records yet. Use Add dialect to create one.
                      </td>
                    </tr>
                  ) : (
                    latestDialects.map((row) => (
                      <tr
                        key={row.id}
                        className="border-b border-app-border/70 dark:border-gray-800 hover:bg-app-bg/40 dark:hover:bg-gray-800/30"
                      >
                        <td className="px-4 py-3 align-top font-mono text-xs">{row.key}</td>
                        <td className="px-4 py-3 align-top font-mono text-xs">{row.language}</td>
                        <td className="px-4 py-3 align-top font-mono text-xs">{row.name}</td>
                        <td className="px-4 py-3 align-top font-mono text-xs">{row.version}</td>
                        <td className="px-4 py-3 align-top whitespace-nowrap">
                          <Badge variant={row.status === 'active' ? 'success' : 'gray'}>
                            {row.status === 'active' ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 align-top text-right">
                          <div className="flex flex-wrap justify-end gap-1.5">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="!p-2 min-h-[2.25rem] min-w-[2.25rem]"
                              onClick={() => openEditDialect(row)}
                              aria-label={`Edit dialect ${row.key}`}
                              title={`Edit · ${row.key}`}
                            >
                              <Pencil className="w-4 h-4 shrink-0" aria-hidden />
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="!p-2 min-h-[2.25rem] min-w-[2.25rem]"
                              onClick={() => setHistoryDialectTarget(row)}
                              aria-label={`Version history · ${row.key}`}
                              title={`History · ${row.key}`}
                            >
                              <History className="w-4 h-4 shrink-0" aria-hidden />
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="!p-2 min-h-[2.25rem] min-w-[2.25rem] text-red-600 border-red-200 hover:bg-red-50 dark:text-red-400 dark:border-red-800 dark:hover:bg-red-950/40"
                              onClick={() => setDeleteDialectTarget(row)}
                              aria-label={`Delete dialect ${row.key}`}
                              title="Delete all versions"
                            >
                              <Trash2 className="w-4 h-4 shrink-0" aria-hidden />
                            </Button>
                            <Button
                              type="button"
                              variant={row.status === 'active' ? 'outline' : 'primary'}
                              size="sm"
                              className="!p-2 min-h-[2.25rem] min-w-[2.25rem]"
                              onClick={() => setConfirmDialect(row)}
                              aria-label={
                                row.status === 'active'
                                  ? `Deactivate dialect ${row.key}`
                                  : `Activate dialect ${row.key}`
                              }
                              title={row.status === 'active' ? 'Deactivate' : 'Activate'}
                            >
                              <Power className="w-4 h-4 shrink-0" aria-hidden />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {createTemplateOpen && (
        <Modal
          isOpen={createTemplateOpen}
          onClose={() => {
            setCreateTemplateOpen(false);
            setCtBindingFormError('');
            setTemplateType('ai_tutor'); // reset to default on close
          }}
          title="Create template"
          size="xl"
        >
          <div className="space-y-4">
            {/* Template Type Switch Menu (as requested by user) */}
            <div>
              <label className="block text-sm font-medium text-app-text dark:text-gray-200 mb-2">Template Type</label>
              <div className="inline-flex rounded-lg border border-app-border dark:border-gray-700 p-1 bg-app-bg dark:bg-gray-900">
                <Button
                  type="button"
                  size="sm"
                  variant={templateType === 'ai_tutor' ? 'primary' : 'outline'}
                  onClick={() => setTemplateType('ai_tutor')}
                  className="rounded-md px-6"
                >
                  AI Tutor Template
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={templateType === 'picture_talk' ? 'primary' : 'outline'}
                  onClick={() => setTemplateType('picture_talk')}
                  className="rounded-md px-6"
                >
                  Picture Talk Template
                </Button>
              </div>
              <p className="text-xs text-app-text-muted dark:text-gray-400 mt-1">
                {templateType === 'ai_tutor'
                  ? 'Lesson or communication tutor prompts (existing behavior)'
                  : 'Dynamic Picture Talk prompts with event handling, scene context, and dialect support'}
              </p>
            </div>

            <Input id="ct-key" label="Key" value={ctKey} onChange={(e) => setCtKey(e.target.value)} className="font-mono text-sm" required />
            <Input id="ct-name" label="Name" value={ctName} onChange={(e) => setCtName(e.target.value)} required />

            {templateType === 'ai_tutor' ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Input id="ct-module" label="Module" value={ctModule} onChange={(e) => setCtModule(e.target.value)} className="font-mono text-sm" />
                  <Input id="ct-sub" label="Submodule" value={ctSubmodule} onChange={(e) => setCtSubmodule(e.target.value)} className="font-mono text-sm" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-app-text dark:text-gray-200 mb-1">Mode</label>
                    <select
                      value={ctMode}
                      onChange={(e) => setCtMode(e.target.value as (typeof MODES)[number])}
                      className="w-full rounded-lg border border-app-border dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                    >
                      {MODES.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </select>
                  </div>
                  <Input
                    id="ct-grade"
                    label="Grade (optional)"
                    value={ctGrade}
                    onChange={(e) => setCtGrade(e.target.value)}
                    disabled={ctAllGradesWildcard}
                  />
                  <Input id="ct-lang" label="Language" value={ctLanguage} onChange={(e) => setCtLanguage(e.target.value)} className="font-mono text-sm" />
                </div>
              </>
            ) : (
              /* Picture Talk specific fields */
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-app-text dark:text-gray-200 mb-1">Module</label>
                    <div
                      className="px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded-lg text-sm font-mono border border-app-border text-app-text dark:text-gray-100"
                      title="Picture Talk templates always use module picture_talk"
                    >
                      picture_talk
                    </div>
                    <p className="text-xs text-app-text-muted dark:text-gray-400 mt-0.5">Fixed for this template type</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-app-text dark:text-gray-200 mb-1">Submodule</label>
                    <select
                      value={ctPictureTalkSubmodule}
                      onChange={(e) => setCtPictureTalkSubmodule(e.target.value as (typeof PICTURE_TALK_SUBMODULES)[number])}
                      className="w-full rounded-lg border border-app-border dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm font-mono"
                    >
                      {PICTURE_TALK_SUBMODULES.map((sub) => (
                        <option key={sub} value={sub}>
                          {sub}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-app-text dark:text-gray-200 mb-1">Event Type</label>
                    <select
                      value={ctEventType}
                      onChange={(e) => setCtEventType(e.target.value as (typeof PICTURE_TALK_EVENT_TYPES)[number])}
                      className="w-full rounded-lg border border-app-border dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm font-mono"
                    >
                      {PICTURE_TALK_EVENT_TYPES.map((evt) => (
                        <option key={evt} value={evt}>
                          {evt}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Input
                    id="ct-grade-pt"
                    label="Grade (optional)"
                    value={ctGrade}
                    onChange={(e) => setCtGrade(e.target.value)}
                    disabled={ctAllGradesWildcard}
                  />
                  <Input id="ct-lang-pt" label="Language" value={ctLanguage} onChange={(e) => setCtLanguage(e.target.value)} className="font-mono text-sm" />
                  <div>
                    <label className="block text-sm font-medium text-app-text dark:text-gray-200 mb-1">Mode</label>
                    <div className="px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded-lg text-sm font-mono border border-app-border">LIVE (fixed for Picture Talk)</div>
                  </div>
                </div>
              </div>
            )}
            <div className="rounded-lg border border-app-border dark:border-gray-600 p-3 space-y-2 bg-app-bg/40 dark:bg-gray-950/40">
              <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-app-text dark:text-gray-100">
                <input
                  type="checkbox"
                  className="rounded border-app-border text-primary-600 focus:ring-primary-500"
                  checked={ctAutoBinding}
                  onChange={(e) => {
                    setCtAutoBinding(e.target.checked);
                    if (!e.target.checked) setCtBindingFormError('');
                  }}
                />
                Auto create / update binding
              </label>
              <p className="text-xs text-app-text-muted dark:text-gray-400 -mt-1">
                When enabled, saving this template will also create or update a matching prompt binding.
              </p>
              {ctAutoBinding && (
                <div className="space-y-3 pt-1 border-t border-app-border/60 dark:border-gray-700 mt-2">
                  <div>
                    <h4 className="text-sm font-semibold text-app-text dark:text-gray-100">Binding settings</h4>
                    <p className="text-xs text-app-text-muted dark:text-gray-400 mt-0.5">
                      Binding decides which template, blocks, and dialect runtime will use for a tenant / grade / mode
                      combination.
                    </p>
                  </div>
                  {ctBindingFormError ? (
                    <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                      {ctBindingFormError}
                    </p>
                  ) : null}
                  <label className="flex items-center gap-2 cursor-pointer select-none text-xs text-app-text dark:text-gray-200">
                    <input
                      type="checkbox"
                      className="rounded border-app-border text-primary-600 focus:ring-primary-500"
                      checked={ctAllowGlobal}
                      onChange={(e) => setCtAllowGlobal(e.target.checked)}
                    />
                    Allow global binding (only if tenant is blank)
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer select-none text-xs text-app-text dark:text-gray-200">
                    <input
                      type="checkbox"
                      className="rounded border-app-border text-primary-600 focus:ring-primary-500"
                      checked={ctAllGradesWildcard}
                      onChange={(e) => {
                        setCtAllGradesWildcard(e.target.checked);
                        if (e.target.checked) setCtGrade('');
                      }}
                    />
                    All grades (wildcard)
                  </label>
                  <div>
                    <label htmlFor="ct-tenant" className="block text-sm font-medium text-app-text dark:text-gray-200 mb-1">
                      Tenant / School
                    </label>
                    <select
                      id="ct-tenant"
                      value={ctTenantId}
                      onChange={(e) => {
                        setCtTenantId(e.target.value);
                        setCtBindingFormError('');
                      }}
                      className={BINDING_SELECT_CLASS}
                      disabled={schoolsLoading}
                    >
                      <option value="">Global / Default (no specific tenant)</option>
                      {schools.map((school) => (
                      <option key={school.id} value={school.code || school.id}>
                        {school.name}
                      </option>
                      ))}
                    </select>
                    {schoolsLoading && <Spinner className="mt-1" size="sm" />}
                    {schoolsError && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1">{schoolsError}</p>
                    )}
                  </div>
                  <Input
                    id="ct-bind-pri"
                    label="Binding priority"
                    value={ctBindingPriority}
                    onChange={(e) => {
                      setCtBindingPriority(e.target.value);
                      setCtBindingFormError('');
                    }}
                    className="font-mono text-xs"
                  />
                  <div>
                    <label htmlFor="ct-dialect-bind" className="block text-sm font-medium text-app-text dark:text-gray-200 mb-1">
                      Default dialect
                    </label>
                    <select
                      id="ct-dialect-bind"
                      value={ctDialectBinding}
                      onChange={(e) => setCtDialectBinding(e.target.value)}
                      className={BINDING_SELECT_CLASS}
                    >
                      <option value="">None (use service default)</option>
                      {latestDialects.map((d) => (
                        <option key={d.id} value={d.key}>
                          {formatDialectSelectLabel(d)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <p className="text-xs text-app-text-muted dark:text-gray-400">
                    Block lists match this template&apos;s module/submodule. Prefer at least <strong>Base rules</strong> so
                    runtime has teacher rules (optional but recommended).
                  </p>
                  {createBindingUiSplit.emphasizeComm ? (
                    <div>
                      <label htmlFor="ct-comm" className="block text-sm font-medium text-app-text dark:text-gray-200 mb-1">
                        Communication rules block
                      </label>
                      <select
                        id="ct-comm"
                        value={ctCommRulesKey}
                        onChange={(e) => setCtCommRulesKey(e.target.value)}
                        className={BINDING_SELECT_CLASS}
                      >
                        <option value="">None</option>
                        {blocksForCreateBinding
                          .filter((b) => b.block_type === 'communication_rules')
                          .map((b) => (
                            <option key={b.id} value={b.key}>
                              {formatBlockSelectLabel(b)}
                            </option>
                          ))}
                      </select>
                    </div>
                  ) : null}
                  {createBindingUiSplit.showLessonSlots ? (
                    <div className="space-y-2">
                      <div>
                        <label htmlFor="ct-br" className="block text-sm font-medium text-app-text dark:text-gray-200 mb-1">
                          Base rules block
                        </label>
                        <select
                          id="ct-br"
                          value={ctBaseRulesKey}
                          onChange={(e) => setCtBaseRulesKey(e.target.value)}
                          className={BINDING_SELECT_CLASS}
                        >
                          <option value="">None</option>
                          {blocksForCreateBinding
                            .filter((b) => b.block_type === 'base_rules')
                            .map((b) => (
                              <option key={b.id} value={b.key}>
                                {formatBlockSelectLabel(b)}
                              </option>
                            ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="ct-ltr" className="block text-sm font-medium text-app-text dark:text-gray-200 mb-1">
                          Lesson type rules block
                        </label>
                        <select
                          id="ct-ltr"
                          value={ctLessonTypeRulesKey}
                          onChange={(e) => setCtLessonTypeRulesKey(e.target.value)}
                          className={BINDING_SELECT_CLASS}
                        >
                          <option value="">None</option>
                          {blocksForCreateBinding
                            .filter((b) => b.block_type === 'lesson_type_rules')
                            .map((b) => (
                              <option key={b.id} value={b.key}>
                                {formatBlockSelectLabel(b)}
                              </option>
                            ))}
                        </select>
                      </div>
                      <div className="rounded-md border border-dashed border-app-border/70 dark:border-gray-600 p-2 space-y-2">
                        <label className="flex items-center gap-2 cursor-pointer select-none text-xs text-app-text dark:text-gray-200">
                          <input
                            type="checkbox"
                            className="rounded border-app-border text-primary-600 focus:ring-primary-500"
                            checked={ctShowDynamicLessonMap}
                            onChange={(e) => setCtShowDynamicLessonMap(e.target.checked)}
                          />
                          Dynamic lesson type mappings (optional)
                        </label>
                        {ctShowDynamicLessonMap ? (
                          <div className="space-y-2 pl-0.5">
                            <p className="text-[11px] text-app-text-muted dark:text-gray-400 leading-snug">
                              One <span className="font-mono">lesson_type_rules</span> block per lesson type. Runtime
                              prefers this map over the single &quot;Lesson type rules block&quot; above. When checked on
                              update, saving sends this map (use all &quot;None&quot; to clear stored mappings).
                            </p>
                            {LESSON_TYPE_RULE_MAP_OPTION_ROWS.map((row) => (
                              <div key={row.canonical}>
                                <label
                                  htmlFor={`ct-ltm-${row.canonical}`}
                                  className="block text-xs font-medium text-app-text dark:text-gray-200 mb-0.5"
                                >
                                  {row.label}
                                </label>
                                <select
                                  id={`ct-ltm-${row.canonical}`}
                                  value={ctLessonTypeRuleSlots[row.canonical]}
                                  onChange={(e) =>
                                    setCtLessonTypeRuleSlots((s) => ({ ...s, [row.canonical]: e.target.value }))
                                  }
                                  className={BINDING_SELECT_CLASS}
                                >
                                  <option value="">None</option>
                                  {blocksForCreateBinding
                                    .filter((b) => b.block_type === 'lesson_type_rules')
                                    .map((b) => (
                                      <option key={b.id} value={b.key}>
                                        {formatBlockSelectLabel(b)}
                                      </option>
                                    ))}
                                </select>
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </div>
                      <div>
                        <label htmlFor="ct-mr" className="block text-sm font-medium text-app-text dark:text-gray-200 mb-1">
                          Mode rules block
                        </label>
                        <select
                          id="ct-mr"
                          value={ctModeRulesKey}
                          onChange={(e) => setCtModeRulesKey(e.target.value)}
                          className={BINDING_SELECT_CLASS}
                        >
                          <option value="">None</option>
                          {blocksForCreateBinding
                            .filter((b) => b.block_type === 'mode_rules')
                            .map((b) => (
                              <option key={b.id} value={b.key}>
                                {formatBlockSelectLabel(b)}
                              </option>
                            ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="ct-sr" className="block text-sm font-medium text-app-text dark:text-gray-200 mb-1">
                          Source rules block
                        </label>
                        <select
                          id="ct-sr"
                          value={ctSourceRulesKey}
                          onChange={(e) => setCtSourceRulesKey(e.target.value)}
                          className={BINDING_SELECT_CLASS}
                        >
                          <option value="">None</option>
                          {blocksForCreateBinding
                            .filter((b) => b.block_type === 'source_rules')
                            .map((b) => (
                              <option key={b.id} value={b.key}>
                                {formatBlockSelectLabel(b)}
                              </option>
                            ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="ct-foc" className="block text-sm font-medium text-app-text dark:text-gray-200 mb-1">
                          Final output check block
                        </label>
                        <select
                          id="ct-foc"
                          value={ctFinalOutputKey}
                          onChange={(e) => setCtFinalOutputKey(e.target.value)}
                          className={BINDING_SELECT_CLASS}
                        >
                          <option value="">None</option>
                          {blocksForCreateBinding
                            .filter((b) => b.block_type === 'final_output_check')
                            .map((b) => (
                              <option key={b.id} value={b.key}>
                                {formatBlockSelectLabel(b)}
                              </option>
                            ))}
                        </select>
                      </div>
                    </div>
                  ) : null}
                  {createBindingUiSplit.showCommSlot && !createBindingUiSplit.emphasizeComm ? (
                    <div>
                      <label htmlFor="ct-cr-opt" className="block text-sm font-medium text-app-text dark:text-gray-200 mb-1">
                        Communication rules block (optional)
                      </label>
                      <select
                        id="ct-cr-opt"
                        value={ctCommRulesKey}
                        onChange={(e) => setCtCommRulesKey(e.target.value)}
                        className={BINDING_SELECT_CLASS}
                      >
                        <option value="">None</option>
                        {blocksForCreateBinding
                          .filter((b) => b.block_type === 'communication_rules')
                          .map((b) => (
                            <option key={b.id} value={b.key}>
                              {formatBlockSelectLabel(b)}
                            </option>
                          ))}
                      </select>
                    </div>
                  ) : null}
                  {createBindingUiSplit.isPictureTalk ? (
                    <div className="space-y-2">
                      <p className="text-xs text-app-text-muted dark:text-gray-400">
                        Picture Talk binding slots. Pick blocks for each Picture Talk role below.
                        Dialect text is automatically taken from the <strong>Default dialect</strong> field above — no separate dialect block needed.
                      </p>
                      <div>
                        <label htmlFor="ct-pt-br" className="block text-sm font-medium text-app-text dark:text-gray-200 mb-1">
                          Base rules block
                        </label>
                        <select
                          id="ct-pt-br"
                          value={ctBaseRulesKey}
                          onChange={(e) => setCtBaseRulesKey(e.target.value)}
                          className={BINDING_SELECT_CLASS}
                        >
                          <option value="">None</option>
                          {blocksForCreateBinding
                            .filter((b) => b.block_type === 'base_rules')
                            .map((b) => (
                              <option key={b.id} value={b.key}>
                                {formatBlockSelectLabel(b)}
                              </option>
                            ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="ct-pt-ptr" className="block text-sm font-medium text-app-text dark:text-gray-200 mb-1">
                          Picture Talk rules block
                        </label>
                        <select
                          id="ct-pt-ptr"
                          value={ctPictureTalkRulesKey}
                          onChange={(e) => setCtPictureTalkRulesKey(e.target.value)}
                          className={BINDING_SELECT_CLASS}
                        >
                          <option value="">None</option>
                          {blocksForCreateBinding
                            .filter((b) => b.block_type === 'picture_talk_rules')
                            .map((b) => (
                              <option key={b.id} value={b.key}>
                                {formatBlockSelectLabel(b)}
                              </option>
                            ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="ct-pt-er" className="block text-sm font-medium text-app-text dark:text-gray-200 mb-1">
                          Event rules block
                        </label>
                        <select
                          id="ct-pt-er"
                          value={ctEventRulesKey}
                          onChange={(e) => setCtEventRulesKey(e.target.value)}
                          className={BINDING_SELECT_CLASS}
                        >
                          <option value="">None</option>
                          {blocksForCreateBinding
                            .filter((b) => b.block_type === 'event_rules')
                            .map((b) => (
                              <option key={b.id} value={b.key}>
                                {formatBlockSelectLabel(b)}
                              </option>
                            ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="ct-pt-sr" className="block text-sm font-medium text-app-text dark:text-gray-200 mb-1">
                          Source rules block (optional)
                        </label>
                        <select
                          id="ct-pt-sr"
                          value={ctSourceRulesKey}
                          onChange={(e) => setCtSourceRulesKey(e.target.value)}
                          className={BINDING_SELECT_CLASS}
                        >
                          <option value="">None</option>
                          {blocksForCreateBinding
                            .filter((b) => b.block_type === 'source_rules')
                            .map((b) => (
                              <option key={b.id} value={b.key}>
                                {formatBlockSelectLabel(b)}
                              </option>
                            ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="ct-pt-foc" className="block text-sm font-medium text-app-text dark:text-gray-200 mb-1">
                          Final output check block (optional)
                        </label>
                        <select
                          id="ct-pt-foc"
                          value={ctFinalOutputKey}
                          onChange={(e) => setCtFinalOutputKey(e.target.value)}
                          className={BINDING_SELECT_CLASS}
                        >
                          <option value="">None</option>
                          {blocksForCreateBinding
                            .filter((b) => b.block_type === 'final_output_check')
                            .map((b) => (
                              <option key={b.id} value={b.key}>
                                {formatBlockSelectLabel(b)}
                              </option>
                            ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="ct-pt-safety" className="block text-sm font-medium text-app-text dark:text-gray-200 mb-1">
                          Safety rules block (optional)
                        </label>
                        <select
                          id="ct-pt-safety"
                          value={ctSafetyRulesKey || ''}
                          onChange={(e) => setCtSafetyRulesKey(e.target.value)}
                          className={BINDING_SELECT_CLASS}
                        >
                          <option value="">None (use default safety rules)</option>
                          {blocksForCreateBinding
                            .filter((b) => b.block_type === 'safety_rules')
                            .map((b) => (
                              <option key={b.id} value={b.key}>
                                {formatBlockSelectLabel(b)}
                              </option>
                            ))}
                        </select>
                      </div>
                    </div>
                  ) : null}
                  <label className="flex items-center gap-2 cursor-pointer select-none text-xs text-app-text dark:text-gray-200">
                    <input
                      type="checkbox"
                      className="rounded border-app-border text-primary-600 focus:ring-primary-500"
                      checked={ctOverwriteBinding}
                      onChange={(e) => setCtOverwriteBinding(e.target.checked)}
                    />
                    Overwrite existing block mappings (replace with only the slots selected above)
                  </label>
                </div>
              )}
            </div>
            <div>
              <label htmlFor="ct-body" className="block text-sm font-medium text-app-text dark:text-gray-200 mb-1">
                Template body
              </label>
              <div id="ct-body">
                <PromptEditor
                  syncKey={createTemplateOpen ? 'template-create' : 'template-create-closed'}
                  value={ctBody}
                  onChange={setCtBody}
                  height="min(360px, 50vh)"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setCreateTemplateOpen(false);
                  setCtBindingFormError('');
                }}
                disabled={savingCreateTemplate}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={() => void runCreateTemplate()}
                isLoading={savingCreateTemplate}
                disabled={!ctKey.trim() || !ctName.trim() || !ctBody.trim()}
              >
                Create
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {createBlockOpen && (
        <Modal
          isOpen={createBlockOpen}
          onClose={() => {
            setCreateBlockOpen(false);
            setCbModule(DEFAULT_AI_TUTOR_MODULE);
          }}
          title="Create block"
          size="xl"
        >
          <div className="space-y-3">
            <Input id="cb-key" label="Key" value={cbKey} onChange={(e) => setCbKey(e.target.value)} className="font-mono text-sm" required />
            <Input id="cb-name" label="Name" value={cbName} onChange={(e) => setCbName(e.target.value)} required />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input
                id="cb-mod"
                label="Module"
                value={cbModule}
                onChange={(e) => setCbModule(e.target.value)}
                className="font-mono text-sm"
                placeholder={DEFAULT_AI_TUTOR_MODULE}
                helperText={`Default for new blocks: ${DEFAULT_AI_TUTOR_MODULE} · use picture_talk for Picture Talk blocks`}
              />
              <Input
                id="cb-sub"
                label="Submodule"
                value={cbSubmodule}
                onChange={(e) => setCbSubmodule(e.target.value)}
                className="font-mono text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-app-text dark:text-gray-200 mb-1">Block type</label>
              <select
                value={cbBlockType}
                onChange={(e) => setCbBlockType(e.target.value as (typeof BLOCK_TYPES)[number])}
                className="w-full rounded-lg border border-app-border dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm font-sans text-app-text dark:text-gray-100"
              >
                {BLOCK_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {BLOCK_TYPE_LABELS[t]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="cb-content" className="block text-sm font-medium text-app-text dark:text-gray-200 mb-1">
                Instruction block
              </label>
              <textarea
                id="cb-content"
                value={cbContent}
                onChange={(e) => setCbContent(e.target.value)}
                rows={10}
                className="w-full rounded-lg border border-app-border dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm font-mono min-h-[200px]"
                required
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setCreateBlockOpen(false);
                  setCbModule(DEFAULT_AI_TUTOR_MODULE);
                }}
                disabled={savingCreateBlock}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={() => void runCreateBlock()}
                isLoading={savingCreateBlock}
                disabled={!cbKey.trim() || !cbName.trim() || !cbContent.trim()}
              >
                Create
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {createDialectOpen && (
        <Modal isOpen={createDialectOpen} onClose={() => setCreateDialectOpen(false)} title="Create dialect profile" size="xl">
          <div className="space-y-3">
            <Input id="cd-key" label="Key" value={cdKey} onChange={(e) => setCdKey(e.target.value)} className="font-mono text-sm" required />
            <Input id="cd-name" label="Name" value={cdName} onChange={(e) => setCdName(e.target.value)} required />
            <Input id="cd-lang" label="Language" value={cdLanguage} onChange={(e) => setCdLanguage(e.target.value)} className="font-mono text-sm" />
            <Input id="cd-desc" label="Description (optional)" value={cdDescription} onChange={(e) => setCdDescription(e.target.value)} />
            <div>
              <label htmlFor="cd-inst" className="block text-sm font-medium text-app-text dark:text-gray-200 mb-1">
                Instruction block
              </label>
              <textarea
                id="cd-inst"
                value={cdInstruction}
                onChange={(e) => setCdInstruction(e.target.value)}
                rows={10}
                className="w-full rounded-lg border border-app-border dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm font-mono min-h-[200px]"
                required
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setCreateDialectOpen(false)} disabled={savingCreateDialect}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={() => void runCreateDialect()}
                isLoading={savingCreateDialect}
                disabled={!cdKey.trim() || !cdName.trim() || !cdInstruction.trim()}
              >
                Create
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {editing && (
        <Modal
          isOpen={Boolean(editing)}
          onClose={() => {
            setEditing(null);
            setEtBindingFormError('');
          }}
          title={`Edit template · ${editing.key}`}
          size="xl"
        >
          <div className="space-y-3">
            <Input
              id="prompt-template-name"
              label="Name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="w-full"
            />
            <div>
              <label htmlFor="prompt-template-body" className="block text-sm font-medium text-app-text dark:text-gray-200 mb-1">
                Template
              </label>
              <div id="prompt-template-body">
                <PromptEditor
                  syncKey={editing.id}
                  value={editTemplate}
                  onChange={setEditTemplate}
                  height="min(400px, 55vh)"
                />
              </div>
            </div>
            <div className="rounded-lg border border-app-border dark:border-gray-600 p-3 space-y-2 bg-app-bg/40 dark:bg-gray-950/40">
              <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-app-text dark:text-gray-100">
                <input
                  type="checkbox"
                  className="rounded border-app-border text-primary-600 focus:ring-primary-500"
                  checked={etAutoBinding}
                  onChange={(e) => {
                    setEtAutoBinding(e.target.checked);
                    if (!e.target.checked) setEtBindingFormError('');
                  }}
                />
                Auto create / update binding
              </label>
              <p className="text-xs text-app-text-muted dark:text-gray-400 -mt-1">
                When enabled, saving will upsert the binding for this template&apos;s module, submodule, mode, grade, and
                language.
              </p>
              {etAutoBinding && (
                <div className="space-y-3 pt-1 border-t border-app-border/60 dark:border-gray-700 mt-2">
                  <h4 className="text-sm font-semibold text-app-text dark:text-gray-100">Binding settings</h4>
                  <p className="text-xs text-app-text-muted dark:text-gray-400">
                    Binding decides which template, blocks, and dialect runtime will use for this row&apos;s scope.
                  </p>
                  {etBindingFormError ? (
                    <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                      {etBindingFormError}
                    </p>
                  ) : null}
                  <label className="flex items-center gap-2 cursor-pointer select-none text-xs text-app-text dark:text-gray-200">
                    <input
                      type="checkbox"
                      className="rounded border-app-border text-primary-600 focus:ring-primary-500"
                      checked={etAllowGlobal}
                      onChange={(e) => setEtAllowGlobal(e.target.checked)}
                    />
                    Allow global binding (only if tenant is blank)
                  </label>
                  <div>
                    <label htmlFor="et-tenant" className="block text-sm font-medium text-app-text dark:text-gray-200 mb-1">
                      Tenant / School
                    </label>
                    <select
                      id="et-tenant"
                      value={etTenantId}
                      onChange={(e) => {
                        setEtTenantId(e.target.value);
                        setEtBindingFormError('');
                      }}
                      className={BINDING_SELECT_CLASS}
                      disabled={schoolsLoading}
                    >
                      <option value="">Global / Default (no specific tenant)</option>
                      {schools.map((school) => (
                      <option key={school.id} value={school.code || school.id}>
                        {school.name}
                      </option>
                      ))}
                    </select>
                    {schoolsLoading && <Spinner className="mt-1" size="sm" />}
                    {schoolsError && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1">{schoolsError}</p>
                    )}
                  </div>
                  <Input
                    id="et-pri"
                    label="Binding priority"
                    value={etBindingPriority}
                    onChange={(e) => {
                      setEtBindingPriority(e.target.value);
                      setEtBindingFormError('');
                    }}
                    className="font-mono text-xs"
                  />
                  <div>
                    <label htmlFor="et-dialect" className="block text-sm font-medium text-app-text dark:text-gray-200 mb-1">
                      Default dialect
                    </label>
                    <select
                      id="et-dialect"
                      value={etDialectBinding}
                      onChange={(e) => setEtDialectBinding(e.target.value)}
                      className={BINDING_SELECT_CLASS}
                    >
                      <option value="">None</option>
                      {latestDialects.map((d) => (
                        <option key={d.id} value={d.key}>
                          {formatDialectSelectLabel(d)}
                        </option>
                      ))}
                    </select>
                  </div>
                  {editBindingUiSplit.emphasizeComm ? (
                    <div>
                      <label htmlFor="et-comm" className="block text-sm font-medium text-app-text dark:text-gray-200 mb-1">
                        Communication rules block
                      </label>
                      <select
                        id="et-comm"
                        value={etCommRulesKey}
                        onChange={(e) => setEtCommRulesKey(e.target.value)}
                        className={BINDING_SELECT_CLASS}
                      >
                        <option value="">None</option>
                        {blocksForEditBinding
                          .filter((b) => b.block_type === 'communication_rules')
                          .map((b) => (
                            <option key={b.id} value={b.key}>
                              {formatBlockSelectLabel(b)}
                            </option>
                          ))}
                      </select>
                    </div>
                  ) : null}
                  {editBindingUiSplit.showLessonSlots ? (
                    <div className="space-y-2">
                      <div>
                        <label htmlFor="et-br" className="block text-sm font-medium text-app-text dark:text-gray-200 mb-1">
                          Base rules block
                        </label>
                        <select
                          id="et-br"
                          value={etBaseRulesKey}
                          onChange={(e) => setEtBaseRulesKey(e.target.value)}
                          className={BINDING_SELECT_CLASS}
                        >
                          <option value="">None</option>
                          {blocksForEditBinding
                            .filter((b) => b.block_type === 'base_rules')
                            .map((b) => (
                              <option key={b.id} value={b.key}>
                                {formatBlockSelectLabel(b)}
                              </option>
                            ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="et-ltr" className="block text-sm font-medium text-app-text dark:text-gray-200 mb-1">
                          Lesson type rules block
                        </label>
                        <select
                          id="et-ltr"
                          value={etLessonTypeRulesKey}
                          onChange={(e) => setEtLessonTypeRulesKey(e.target.value)}
                          className={BINDING_SELECT_CLASS}
                        >
                          <option value="">None</option>
                          {blocksForEditBinding
                            .filter((b) => b.block_type === 'lesson_type_rules')
                            .map((b) => (
                              <option key={b.id} value={b.key}>
                                {formatBlockSelectLabel(b)}
                              </option>
                            ))}
                        </select>
                      </div>
                      <div className="rounded-md border border-dashed border-app-border/70 dark:border-gray-600 p-2 space-y-2">
                        <label className="flex items-center gap-2 cursor-pointer select-none text-xs text-app-text dark:text-gray-200">
                          <input
                            type="checkbox"
                            className="rounded border-app-border text-primary-600 focus:ring-primary-500"
                            checked={etShowDynamicLessonMap}
                            onChange={(e) => setEtShowDynamicLessonMap(e.target.checked)}
                          />
                          Dynamic lesson type mappings (optional)
                        </label>
                        {etShowDynamicLessonMap ? (
                          <div className="space-y-2 pl-0.5">
                            <p className="text-[11px] text-app-text-muted dark:text-gray-400 leading-snug">
                              One <span className="font-mono">lesson_type_rules</span> block per lesson type. Runtime
                              prefers this map over the single &quot;Lesson type rules block&quot; above. When checked on
                              update, saving sends this map (use all &quot;None&quot; to clear stored mappings).
                            </p>
                            {LESSON_TYPE_RULE_MAP_OPTION_ROWS.map((row) => (
                              <div key={row.canonical}>
                                <label
                                  htmlFor={`et-ltm-${row.canonical}`}
                                  className="block text-xs font-medium text-app-text dark:text-gray-200 mb-0.5"
                                >
                                  {row.label}
                                </label>
                                <select
                                  id={`et-ltm-${row.canonical}`}
                                  value={etLessonTypeRuleSlots[row.canonical]}
                                  onChange={(e) =>
                                    setEtLessonTypeRuleSlots((s) => ({ ...s, [row.canonical]: e.target.value }))
                                  }
                                  className={BINDING_SELECT_CLASS}
                                >
                                  <option value="">None</option>
                                  {blocksForEditBinding
                                    .filter((b) => b.block_type === 'lesson_type_rules')
                                    .map((b) => (
                                      <option key={b.id} value={b.key}>
                                        {formatBlockSelectLabel(b)}
                                      </option>
                                    ))}
                                </select>
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </div>
                      <div>
                        <label htmlFor="et-mr" className="block text-sm font-medium text-app-text dark:text-gray-200 mb-1">
                          Mode rules block
                        </label>
                        <select
                          id="et-mr"
                          value={etModeRulesKey}
                          onChange={(e) => setEtModeRulesKey(e.target.value)}
                          className={BINDING_SELECT_CLASS}
                        >
                          <option value="">None</option>
                          {blocksForEditBinding
                            .filter((b) => b.block_type === 'mode_rules')
                            .map((b) => (
                              <option key={b.id} value={b.key}>
                                {formatBlockSelectLabel(b)}
                              </option>
                            ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="et-sr" className="block text-sm font-medium text-app-text dark:text-gray-200 mb-1">
                          Source rules block
                        </label>
                        <select
                          id="et-sr"
                          value={etSourceRulesKey}
                          onChange={(e) => setEtSourceRulesKey(e.target.value)}
                          className={BINDING_SELECT_CLASS}
                        >
                          <option value="">None</option>
                          {blocksForEditBinding
                            .filter((b) => b.block_type === 'source_rules')
                            .map((b) => (
                              <option key={b.id} value={b.key}>
                                {formatBlockSelectLabel(b)}
                              </option>
                            ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="et-foc" className="block text-sm font-medium text-app-text dark:text-gray-200 mb-1">
                          Final output check block
                        </label>
                        <select
                          id="et-foc"
                          value={etFinalOutputKey}
                          onChange={(e) => setEtFinalOutputKey(e.target.value)}
                          className={BINDING_SELECT_CLASS}
                        >
                          <option value="">None</option>
                          {blocksForEditBinding
                            .filter((b) => b.block_type === 'final_output_check')
                            .map((b) => (
                              <option key={b.id} value={b.key}>
                                {formatBlockSelectLabel(b)}
                              </option>
                            ))}
                        </select>
                      </div>
                    </div>
                  ) : null}
                  {editBindingUiSplit.showCommSlot && !editBindingUiSplit.emphasizeComm ? (
                    <div>
                      <label htmlFor="et-cr" className="block text-sm font-medium text-app-text dark:text-gray-200 mb-1">
                        Communication rules block (optional)
                      </label>
                      <select
                        id="et-cr"
                        value={etCommRulesKey}
                        onChange={(e) => setEtCommRulesKey(e.target.value)}
                        className={BINDING_SELECT_CLASS}
                      >
                        <option value="">None</option>
                        {blocksForEditBinding
                          .filter((b) => b.block_type === 'communication_rules')
                          .map((b) => (
                            <option key={b.id} value={b.key}>
                              {formatBlockSelectLabel(b)}
                            </option>
                          ))}
                      </select>
                    </div>
                  ) : null}
                  {editBindingUiSplit.isPictureTalk ? (
                    <div className="space-y-2">
                      <p className="text-xs text-app-text-muted dark:text-gray-400">
                        Picture Talk binding slots. Pick blocks for each Picture Talk role below.
                        Dialect text is automatically taken from the <strong>Default dialect</strong> field above — no separate dialect block needed.
                      </p>
                      <div>
                        <label htmlFor="et-pt-br" className="block text-sm font-medium text-app-text dark:text-gray-200 mb-1">
                          Base rules block
                        </label>
                        <select
                          id="et-pt-br"
                          value={etBaseRulesKey}
                          onChange={(e) => setEtBaseRulesKey(e.target.value)}
                          className={BINDING_SELECT_CLASS}
                        >
                          <option value="">None</option>
                          {blocksForEditBinding
                            .filter((b) => b.block_type === 'base_rules')
                            .map((b) => (
                              <option key={b.id} value={b.key}>
                                {formatBlockSelectLabel(b)}
                              </option>
                            ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="et-pt-ptr" className="block text-sm font-medium text-app-text dark:text-gray-200 mb-1">
                          Picture Talk rules block
                        </label>
                        <select
                          id="et-pt-ptr"
                          value={etPictureTalkRulesKey}
                          onChange={(e) => setEtPictureTalkRulesKey(e.target.value)}
                          className={BINDING_SELECT_CLASS}
                        >
                          <option value="">None</option>
                          {blocksForEditBinding
                            .filter((b) => b.block_type === 'picture_talk_rules')
                            .map((b) => (
                              <option key={b.id} value={b.key}>
                                {formatBlockSelectLabel(b)}
                              </option>
                            ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="et-pt-er" className="block text-sm font-medium text-app-text dark:text-gray-200 mb-1">
                          Event rules block
                        </label>
                        <select
                          id="et-pt-er"
                          value={etEventRulesKey}
                          onChange={(e) => setEtEventRulesKey(e.target.value)}
                          className={BINDING_SELECT_CLASS}
                        >
                          <option value="">None</option>
                          {blocksForEditBinding
                            .filter((b) => b.block_type === 'event_rules')
                            .map((b) => (
                              <option key={b.id} value={b.key}>
                                {formatBlockSelectLabel(b)}
                              </option>
                            ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="et-pt-sr" className="block text-sm font-medium text-app-text dark:text-gray-200 mb-1">
                          Source rules block (optional)
                        </label>
                        <select
                          id="et-pt-sr"
                          value={etSourceRulesKey}
                          onChange={(e) => setEtSourceRulesKey(e.target.value)}
                          className={BINDING_SELECT_CLASS}
                        >
                          <option value="">None</option>
                          {blocksForEditBinding
                            .filter((b) => b.block_type === 'source_rules')
                            .map((b) => (
                              <option key={b.id} value={b.key}>
                                {formatBlockSelectLabel(b)}
                              </option>
                            ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="et-pt-foc" className="block text-sm font-medium text-app-text dark:text-gray-200 mb-1">
                          Final output check block (optional)
                        </label>
                        <select
                          id="et-pt-foc"
                          value={etFinalOutputKey}
                          onChange={(e) => setEtFinalOutputKey(e.target.value)}
                          className={BINDING_SELECT_CLASS}
                        >
                          <option value="">None</option>
                          {blocksForEditBinding
                            .filter((b) => b.block_type === 'final_output_check')
                            .map((b) => (
                              <option key={b.id} value={b.key}>
                                {formatBlockSelectLabel(b)}
                              </option>
                            ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="et-pt-safety" className="block text-sm font-medium text-app-text dark:text-gray-200 mb-1">
                          Safety rules block (optional)
                        </label>
                        <select
                          id="et-pt-safety"
                          value={etSafetyRulesKey || ''}
                          onChange={(e) => setEtSafetyRulesKey(e.target.value)}
                          className={BINDING_SELECT_CLASS}
                        >
                          <option value="">None (clear mapping; runtime uses default block)</option>
                          {blocksForEditBinding
                            .filter((b) => b.block_type === 'safety_rules')
                            .map((b) => (
                              <option key={b.id} value={b.key}>
                                {formatBlockSelectLabel(b)}
                              </option>
                            ))}
                        </select>
                      </div>
                    </div>
                  ) : null}
                  <label className="flex items-center gap-2 cursor-pointer select-none text-xs text-app-text dark:text-gray-200">
                    <input
                      type="checkbox"
                      className="rounded border-app-border text-primary-600 focus:ring-primary-500"
                      checked={etOverwriteBinding}
                      onChange={(e) => setEtOverwriteBinding(e.target.checked)}
                    />
                    Overwrite existing block mappings
                  </label>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setEditing(null);
                  setEtBindingFormError('');
                }}
                disabled={savingEdit}
              >
                Cancel
              </Button>
              <Button type="button" variant="primary" onClick={() => void runEditSave()} isLoading={savingEdit}>
                Save
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {editingBlock && (
        <Modal isOpen={Boolean(editingBlock)} onClose={() => setEditingBlock(null)} title={`Edit block · ${editingBlock.key}`} size="xl">
          <div className="space-y-3">
            <Input label="Name" value={editBlockName} onChange={(e) => setEditBlockName(e.target.value)} />
            <div>
              <label htmlFor="edit-block-instruction" className="block text-sm font-medium text-app-text dark:text-gray-200 mb-1">
                Instruction block
              </label>
              <textarea
                id="edit-block-instruction"
                value={editBlockContent}
                onChange={(e) => setEditBlockContent(e.target.value)}
                rows={12}
                className="w-full rounded-lg border border-app-border dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm font-mono min-h-[220px]"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setEditingBlock(null)} disabled={savingBlock}>
                Cancel
              </Button>
              <Button type="button" variant="primary" onClick={() => void runEditBlockSave()} isLoading={savingBlock}>
                Save
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {editingDialect && (
        <Modal isOpen={Boolean(editingDialect)} onClose={() => setEditingDialect(null)} title={`Edit dialect · ${editingDialect.key}`} size="xl">
          <div className="space-y-3">
            <Input label="Name" value={editDialectName} onChange={(e) => setEditDialectName(e.target.value)} />
            <Input label="Description" value={editDialectDescription} onChange={(e) => setEditDialectDescription(e.target.value)} />
            <div>
              <label className="block text-sm font-medium text-app-text dark:text-gray-200 mb-1">Instruction block</label>
              <textarea
                value={editDialectInstruction}
                onChange={(e) => setEditDialectInstruction(e.target.value)}
                rows={12}
                className="w-full rounded-lg border border-app-border dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm font-mono min-h-[220px]"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setEditingDialect(null)} disabled={savingDialect}>
                Cancel
              </Button>
              <Button type="button" variant="primary" onClick={() => void runEditDialectSave()} isLoading={savingDialect}>
                Save
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {historyTarget && (
        <Modal isOpen={Boolean(historyTarget)} onClose={() => setHistoryTarget(null)} title={`History · ${historyTarget.key}`} size="xl">
          {historyRows.length === 0 ? (
            <p className="text-sm text-app-text-muted">No versions found.</p>
          ) : (
            <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-1">
              {historyRows.map((item) => (
                <div key={item.id} className="rounded-lg border border-app-border dark:border-gray-700 p-4 bg-app-bg/50 dark:bg-gray-900/40">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="gray">v{item.version}</Badge>
                      <Badge variant={item.status === 'active' ? 'success' : 'gray'}>{item.status}</Badge>
                      <span className="text-xs font-mono text-app-text-muted">{item.mode}</span>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant={historyTemplateRuntimeId === item.id ? 'outline' : 'primary'}
                      disabled={historyTemplateRuntimeId === item.id || activatingId === item.id}
                      isLoading={activatingId === item.id}
                      onClick={() => void runActivateTemplateVersion(item.id)}
                    >
                      {historyTemplateRuntimeId === item.id ? 'Active (runtime)' : 'Make active'}
                    </Button>
                  </div>
                  <p className="mt-3 text-sm font-medium text-app-text dark:text-gray-100">{item.name}</p>
                  <label className="mt-2 block text-xs font-medium text-app-text-muted">Template body</label>
                  <textarea
                    readOnly
                    value={item.template ?? ''}
                    rows={14}
                    className="mt-1 w-full rounded-lg border border-app-border dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-xs font-mono text-app-text dark:text-gray-100 resize-y min-h-[200px]"
                  />
                </div>
              ))}
            </div>
          )}
        </Modal>
      )}

      {historyBlockTarget && (
        <Modal isOpen={Boolean(historyBlockTarget)} onClose={() => setHistoryBlockTarget(null)} title={`History · ${historyBlockTarget.key}`} size="xl">
          {historyBlockRows.length === 0 ? (
            <p className="text-sm text-app-text-muted">No versions found.</p>
          ) : (
            <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-1">
              {historyBlockRows.map((item) => (
                <div key={item.id} className="rounded-lg border border-app-border dark:border-gray-700 p-4 bg-app-bg/50 dark:bg-gray-900/40">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="gray">v{item.version}</Badge>
                      <Badge variant={item.status === 'active' ? 'success' : 'gray'}>{item.status}</Badge>
                      <span className="text-xs font-mono text-app-text-muted">{item.block_type}</span>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant={historyBlockRuntimeId === item.id ? 'outline' : 'primary'}
                      disabled={historyBlockRuntimeId === item.id || activatingId === item.id}
                      isLoading={activatingId === item.id}
                      onClick={() => void runActivateBlockVersion(item.id)}
                    >
                      {historyBlockRuntimeId === item.id ? 'Active (runtime)' : 'Make active'}
                    </Button>
                  </div>
                  <p className="mt-3 text-sm font-medium">{item.name}</p>
                  <label className="mt-2 block text-xs font-medium text-app-text-muted">Instruction block</label>
                  <textarea
                    readOnly
                    value={item.content ?? ''}
                    rows={14}
                    className="mt-1 w-full rounded-lg border border-app-border dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-xs font-mono min-h-[200px] resize-y"
                  />
                </div>
              ))}
            </div>
          )}
        </Modal>
      )}

      {historyDialectTarget && (
        <Modal
          isOpen={Boolean(historyDialectTarget)}
          onClose={() => setHistoryDialectTarget(null)}
          title={`History · ${historyDialectTarget.key}`}
          size="xl"
        >
          {historyDialectRows.length === 0 ? (
            <p className="text-sm text-app-text-muted">No versions found.</p>
          ) : (
            <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-1">
              {historyDialectRows.map((item) => (
                <div key={item.id} className="rounded-lg border border-app-border dark:border-gray-700 p-4 bg-app-bg/50 dark:bg-gray-900/40">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="gray">v{item.version}</Badge>
                      <Badge variant={item.status === 'active' ? 'success' : 'gray'}>{item.status}</Badge>
                      <span className="text-xs text-app-text-muted">{item.language}</span>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant={historyDialectRuntimeId === item.id ? 'outline' : 'primary'}
                      disabled={historyDialectRuntimeId === item.id || activatingId === item.id}
                      isLoading={activatingId === item.id}
                      onClick={() => void runActivateDialectVersion(item.id)}
                    >
                      {historyDialectRuntimeId === item.id ? 'Active (runtime)' : 'Make active'}
                    </Button>
                  </div>
                  <p className="mt-3 text-sm font-medium">{item.name}</p>
                  {item.description ? (
                    <p className="mt-1 text-xs text-app-text-muted">{item.description}</p>
                  ) : null}
                  <label className="mt-2 block text-xs font-medium text-app-text-muted">Instruction block</label>
                  <textarea
                    readOnly
                    value={item.instruction_block ?? ''}
                    rows={14}
                    className="mt-1 w-full rounded-lg border border-app-border dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-xs font-mono min-h-[200px] resize-y"
                  />
                </div>
              ))}
            </div>
          )}
        </Modal>
      )}

      {confirmTemplate && (
        <ConfirmDialog
          title={confirmTemplate.status === 'active' ? 'Deactivate template?' : 'Activate template?'}
          message={
            confirmTemplate.status === 'active'
              ? 'Are you sure you want to deactivate this template?'
              : 'Activate this template?'
          }
          confirmLabel={confirmTemplate.status === 'active' ? 'Deactivate' : 'Activate'}
          danger={confirmTemplate.status === 'active'}
          onCancel={() => setConfirmTemplate(null)}
          onConfirm={() => void runToggleTemplate()}
        />
      )}

      {confirmBlock && (
        <ConfirmDialog
          title={confirmBlock.status === 'active' ? 'Deactivate block?' : 'Activate block?'}
          message={
            confirmBlock.status === 'active'
              ? 'Are you sure you want to deactivate this prompt block?'
              : 'Activate this prompt block?'
          }
          confirmLabel={confirmBlock.status === 'active' ? 'Deactivate' : 'Activate'}
          danger={confirmBlock.status === 'active'}
          onCancel={() => setConfirmBlock(null)}
          onConfirm={() => void runToggleBlock()}
        />
      )}

      {confirmDialect && (
        <ConfirmDialog
          title={confirmDialect.status === 'active' ? 'Deactivate dialect?' : 'Activate dialect?'}
          message={
            confirmDialect.status === 'active'
              ? 'Are you sure you want to deactivate this dialect profile?'
              : 'Activate this dialect profile?'
          }
          confirmLabel={confirmDialect.status === 'active' ? 'Deactivate' : 'Activate'}
          danger={confirmDialect.status === 'active'}
          onCancel={() => setConfirmDialect(null)}
          onConfirm={() => void runToggleDialect()}
        />
      )}

      {deleteTemplateTarget && (
        <ConfirmDialog
          title="Delete template?"
          message={`This permanently removes all versions of this template (${deleteTemplateTarget.key} — ${deleteTemplateTarget.name}). Runtime will fall back to file/code defaults if applicable. This cannot be undone.`}
          confirmLabel="Delete"
          danger
          isConfirmLoading={savingDelete}
          onCancel={() => setDeleteTemplateTarget(null)}
          onConfirm={() => void runDeleteTemplate()}
        />
      )}

      {deleteBlockTarget && (
        <ConfirmDialog
          title="Delete prompt block?"
          message={`This permanently removes all versions of this block (${deleteBlockTarget.key} — ${deleteBlockTarget.name}). This cannot be undone.`}
          confirmLabel="Delete"
          danger
          isConfirmLoading={savingDelete}
          onCancel={() => setDeleteBlockTarget(null)}
          onConfirm={() => void runDeleteBlock()}
        />
      )}

      {deleteDialectTarget && (
        <ConfirmDialog
          title="Delete dialect profile?"
          message={`This permanently removes all versions of this dialect (${deleteDialectTarget.key} — ${deleteDialectTarget.name}). This cannot be undone.`}
          confirmLabel="Delete"
          danger
          isConfirmLoading={savingDelete}
          onCancel={() => setDeleteDialectTarget(null)}
          onConfirm={() => void runDeleteDialect()}
        />
      )}
    </Layout>
  );
};
